import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowDown,
  BadgeCheck,
  Bell,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  CircleDollarSign,
  Coins,
  Copy,
  Download,
  ExternalLink,
  Gauge,
  Gem,
  Gift,
  Hand,
  ImagePlus,
  Layers,
  List,
  LockKeyhole,
  Mail,
  Megaphone,
  LogIn,
  LogOut,
  Loader2,
  MessageCircle,
  Monitor,
  Medal,
  Palette,
  Paperclip,
  RectangleHorizontal,
  RectangleVertical,
  Send,
  Shuffle,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Square,
  Star,
  Smartphone,
  Trash2,
  UploadCloud,
  UserPlus,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  bulkDeleteAccountNotifications,
  deleteGenerationHistoryItem,
  deleteAccountNotification,
  fetchAccountCreditsLedger,
  fetchAccountNotifications,
  fetchAuthStatus,
  readStoredAuthStatus,
  writeStoredAuthStatus,
  fetchGenerationHistory,
  fetchGenerationTaskResult,
  fetchPublicSettings,
  generateImageStream,
  normalizeChatModels,
  loginUser,
  logoutUser,
  normalizeModels,
  normalizeTitle,
  markAccountNotificationRead,
  markAccountNotificationsReadAll,
  redeemCode,
  registerUser,
  sendChatCompletion,
  updateAccountEmail,
  updateAccountPassword,
  updateAccountPhone,
  uploadChatAttachment,
} from "./api.js";
import { ListboxField } from "./components/ui/ListboxField.jsx";
import { ToastStack, useToastQueue } from "./components/ui/Toast.jsx";
import { GalleryLightbox } from "./features/gallery/GalleryLightbox.jsx";
import { GalleryWorkspace } from "./features/gallery/GalleryWorkspace.jsx";
import { HistoryStrip } from "./features/history/HistoryStrip.jsx";
import { AppSidebar } from "./layout/AppSidebar.jsx";
import { formatDateTime, formatTimestamp, conversationTitleFromMessage } from "./lib/format.js";
import { createClientId, getVisitorId } from "./lib/identity.js";
import {
  buildDownloadName,
  expandHistoryItems,
  getDisplayImageSrc,
  getDownloadImageSrc,
  getGenerationTextResult,
  getImageSetItems,
  getPreviewPlaceholderSrc,
  handleImageDownload,
  normalizeHistoryItems,
  purgeCachedHistoryItem,
  thumbnailUrlForDisplay,
  useProgressiveImageSrc,
} from "./lib/imageAssets.js";

const ASPECT_RATIOS = [
  { value: "auto", label: "自动", fallbackMapping: "auto", optionKey: "size_auto", icon: Square },
  { value: "landscape", label: "横屏", fallbackMapping: "landscape", optionKey: "size_landscape", icon: RectangleHorizontal },
  { value: "portrait", label: "竖屏", fallbackMapping: "portrait", optionKey: "size_portrait", icon: RectangleVertical },
  { value: "1:1", label: "方图", fallbackMapping: "1:1", optionKey: "size_square", icon: Square },
];
const IMAGE_COUNT_OPTIONS = [1, 2, 3, 4].map((count) => ({
  value: String(count),
  label: String(count),
  icon: ImagePlus,
}));
const STANDARD_RESOLUTION = "standard";
const DEFAULT_ADVANCED_RESOLUTION_OPTIONS = [
  { value: "standard", label: "标准", icon: Monitor },
];
const ADVANCED_STYLE_OPTIONS = [
  { value: "realistic", label: "realistic", icon: Palette },
  { value: "fantasy", label: "fantasy", icon: Palette },
  { value: "cartoon", label: "cartoon", icon: Palette },
  { value: "cyberpunk", label: "cyberpunk", icon: Palette },
];
const ADVANCED_QUALITY_OPTIONS = [
  { value: "low", label: "low", icon: Gauge },
  { value: "medium", label: "medium", icon: Gauge },
  { value: "high", label: "high", icon: Gauge },
];
const ADVANCED_BACKGROUND_OPTIONS = [
  { value: "auto", label: "auto", icon: Layers },
  { value: "opaque", label: "opaque", icon: Layers },
  { value: "transparent", label: "transparent", icon: Layers },
];
const ADVANCED_BACKGROUND_VALUES = new Set(ADVANCED_BACKGROUND_OPTIONS.map((item) => item.value));
const MODELS_WITHOUT_TRANSPARENT_BACKGROUND = new Set(["gpt-image-2", "codex-gpt-image-2"]);
const ADVANCED_STEP_OPTIONS = [10, 20, 30, 40, 50, 55, 60, 70, 80, 90, 100].map((step) => {
  const value = String(step);
  return { value, label: value, icon: SlidersHorizontal };
});
const ADVANCED_SAMPLER_OPTIONS = [
  { value: "ddim", label: "ddim", icon: Shuffle },
  { value: "plms", label: "plms", icon: Shuffle },
  { value: "k_lms", label: "k_lms", icon: Shuffle },
];
const ADVANCED_HELP_ITEMS = [
  {
    label: "Negative_prompt",
    text: "负向提示词，排除不想在画面中出现元素，可大幅减少干扰元素",
  },
  {
    label: "style",
    text: "风格，\"realistic\", \"fantasy\", \"cartoon\", \"cyberpunk\" 可选",
  },
  {
    label: "quality",
    text: "图片质量，low(低), medium(中), high(高)，高质量可生成更多细节",
  },
  {
    label: "background",
    text: "背景，可选 \"auto\", \"opaque\"，支持透明背景的模型可选 \"transparent\"",
  },
  {
    label: "steps",
    text: "迭代步数，步数越高细节越丰富，但生成慢推荐50-60之间",
  },
  {
    label: "sampler",
    text: "采样算法，\"ddim\", \"plms\", \"k_lms\" 等(建议保持默认)",
  },
];
const MAX_REFERENCE_IMAGES = 4;
const IMAGE_HISTORY_STRIP_LIMIT = 16;
const CHAT_UNAVAILABLE_MESSAGE = "Chat功能暂未开放使用";
const MOBILE_SIDEBAR_QUERY = "(max-width: 820px)";

const THEME_STORAGE_KEY = "frontend-theme";

function getInitialTheme() {
  if (typeof window === "undefined") {
    return "light";
  }
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "light" || storedTheme === "dark") {
      return storedTheme;
    }
  } catch {
    // Ignore storage access failures and fall back to system preference.
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") {
    return;
  }
  const normalizedTheme = theme === "dark" ? "dark" : "light";
  if (document.body) {
    document.body.dataset.theme = normalizedTheme;
  }
  document.documentElement.dataset.theme = normalizedTheme;
  document.documentElement.style.colorScheme = normalizedTheme;
}

const GENERATE_PROGRESS = {
  default: "正在创作中...",
  pending: "正在处理中...",
  completed: "完成！",
  failed: "失败",
  waiting: "等待任务数据...",
  retrying: (current, max) => `连接丢失，正在重试... (${current}/${max})`,
};

const ACCOUNT_CENTER_NAV_ITEMS = [
  { label: "账户概览", icon: UserRound, target: "account-overview", active: true },
  { label: "我的积分", icon: Coins, view: "points" },
  { label: "消息通知", icon: Bell, view: "notifications" },
  { label: "邀请推广", icon: UsersRound, target: "account-invite" },
  { label: "账号设置", icon: ShieldCheck, view: "settings" },
];

const ACCOUNT_CENTER_DEFAULT_VIEW = "overview";
const ACCOUNT_NOTIFICATION_FILTERS = [
  { value: "all", label: "全部" },
  { value: "unread", label: "未读" },
  { value: "read", label: "已读" },
];
const ACCOUNT_NOTIFICATION_TYPE_FILTERS = [
  { value: "all", label: "全部类型" },
  { value: "system", label: "系统通知" },
  { value: "activity", label: "活动通知" },
  { value: "announcement", label: "公告通知" },
  { value: "points", label: "积分提醒" },
];

const ACCOUNT_NOTIFICATION_META = {
  announcement: { icon: Megaphone, label: "\u516C\u544A\u901A\u77E5", tone: "violet" },
  activity: { icon: Gift, label: "\u6D3B\u52A8\u901A\u77E5", tone: "green" },
  points: { icon: Medal, label: "\u79EF\u5206\u63D0\u9192", tone: "amber" },
  system: { icon: Bell, label: "\u7CFB\u7EDF\u901A\u77E5", tone: "blue" },
};

const ACCOUNT_LEDGER_META = {
  activity: { icon: Sparkles, label: "\u6D3B\u52A8\u5956\u52B1", tone: "amber" },
  chat: { icon: MessageCircle, label: "\u5BF9\u8BDD", tone: "blue" },
  expire: { icon: AlertTriangle, label: "\u8FC7\u671F\u56DE\u6536", tone: "orange" },
  image: { icon: ImagePlus, label: "\u6587\u751F\u56FE", tone: "blue" },
  invite: { icon: UsersRound, label: "\u9080\u8BF7\u5956\u52B1", tone: "green" },
  redeem: { icon: Gift, label: "\u5151\u6362\u793C\u54C1", tone: "violet" },
  sign: { icon: Check, label: "\u6BCF\u65E5\u7B7E\u5230", tone: "green" },
  system: { icon: ShieldCheck, label: "\u7CFB\u7EDF\u8865\u507F", tone: "blue" },
  voucher: { icon: Gift, label: "\u5151\u6362\u7801\u5145\u503C", tone: "gold" },
};

function normalizeAccountNotificationType(value) {
  const type = String(value || "").trim().toLowerCase();
  return ACCOUNT_NOTIFICATION_META[type] ? type : "system";
}

function accountNotificationMeta(value) {
  const type = normalizeAccountNotificationType(value);
  return ACCOUNT_NOTIFICATION_META[type] || ACCOUNT_NOTIFICATION_META.system;
}

function accountLedgerMeta(value) {
  const kind = String(value || "").trim().toLowerCase();
  return ACCOUNT_LEDGER_META[kind] || ACCOUNT_LEDGER_META.system;
}

function normalizeAccountNotificationItem(item) {
  const meta = accountNotificationMeta(item?.type || item?.notification_type);
  const tone = String(item?.tone || meta.tone || "blue").trim() || meta.tone || "blue";
  return {
    id: Number(item?.id) || item?.id,
    icon: meta.icon,
    title: normalizeOptionalText(item?.title) || "\u6D88\u606F\u901A\u77E5",
    summary: normalizeOptionalText(item?.summary) || normalizeOptionalText(item?.detail) || "",
    detail: normalizeOptionalText(item?.detail) || normalizeOptionalText(item?.summary) || "",
    type: normalizeAccountNotificationType(item?.type || item?.notification_type),
    typeLabel: meta.label,
    tone,
    time: formatAccountDateTime(item?.created_at || item?.createdAt || item?.published_at || item?.publishedAt),
    unread: !(item?.is_read === true || item?.isRead === true),
    readAt: item?.read_at || item?.readAt || "",
    deletedAt: item?.deleted_at || item?.deletedAt || "",
  };
}

function normalizeAccountNotificationItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => normalizeAccountNotificationItem(item))
    .filter((item) => item.id !== null && item.id !== undefined && item.id !== "");
}

function normalizeAccountLedgerKind(item) {
  const rawKind = String(item?.kind || item?.entry_type || item?.type || "").trim().toLowerCase();
  if (rawKind.startsWith("chat_")) {
    return "chat";
  }
  if (rawKind.startsWith("generation_")) {
    return "image";
  }
  if (rawKind === "redeem_credit") {
    return "redeem";
  }
  return rawKind;
}

function normalizeAccountLedgerItem(item) {
  const kind = normalizeAccountLedgerKind(item);
  const meta = accountLedgerMeta(kind);
  const change = firstFiniteNumber(item?.amount, item?.change, 0);
  const balance = firstFiniteNumber(item?.balance_after, item?.balance, item?.running_balance, 0);
  return {
    id: String(item?.id || `${kind}-${item?.created_at || item?.createdAt || balance}`),
    icon: meta.icon,
    tone: String(item?.tone || meta.tone || "blue").trim() || meta.tone || "blue",
    type: kind || "system",
    typeLabel: meta.label,
    description: normalizeOptionalText(item?.description)
      || normalizeOptionalText(item?.reference_type)
      || normalizeOptionalText(item?.entry_type)
      || meta.label,
    time: formatAccountDateTime(item?.created_at || item?.createdAt),
    change,
    balance,
    direction: String(item?.direction || (change >= 0 ? "income" : "expense")).trim().toLowerCase(),
  };
}

function normalizeAccountLedgerItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => normalizeAccountLedgerItem(item))
    .filter((item) => item.id);
}
const ACCOUNT_NOTIFICATION_ITEMS = [
  {
    id: "points-award",
    icon: Bell,
    title: "积分奖励发放通知",
    summary: "恭喜您获得 100 积分奖励，感谢您对我们的支持！",
    detail: "本次积分已发放到您的账户，可在积分明细中查看记录。后续如有活动奖励，系统也会在这里同步提醒。",
    type: "system",
    typeLabel: "系统通知",
    tone: "blue",
    time: "2026/06/05 20:07",
    unread: true,
  },
  {
    id: "invite-award",
    icon: Gift,
    title: "邀请奖励到账",
    summary: "您成功邀请的好友已完成注册，奖励 50 积分已到账！",
    detail: "邀请推广奖励将按活动规则自动发放，您可以在账户概览或积分明细中继续查看到账情况。",
    type: "activity",
    typeLabel: "活动通知",
    tone: "green",
    time: "2026/06/05 18:30",
    unread: true,
  },
  {
    id: "level-update",
    icon: Gem,
    title: "账户等级变更通知",
    summary: "您的账户等级已升级为管理员，享受更多专属权益！",
    detail: "账号等级更新后，管理权限和部分服务入口会同步变化。请确认当前登录环境是否为本人设备。",
    type: "system",
    typeLabel: "系统通知",
    tone: "violet",
    time: "2026/06/05 16:12",
    unread: true,
  },
  {
    id: "security-reminder",
    icon: ShieldCheck,
    title: "安全设置提醒",
    summary: "为保障账户安全，建议您定期修改密码并开启双重验证。",
    detail: "如果发现异常登录、陌生设备或短信验证码频繁触发，请及时修改密码并联系客服。",
    type: "system",
    typeLabel: "系统通知",
    tone: "orange",
    time: "2026/06/04 22:45",
    unread: false,
  },
  {
    id: "platform-maintenance",
    icon: Megaphone,
    title: "平台维护公告",
    summary: "系统将于 6 月 6 日 02:00 - 04:00 进行维护，期间服务可能受影响。",
    detail: "维护期间可能出现短暂的登录、查询或消息延迟，请提前安排相关操作。",
    type: "announcement",
    typeLabel: "公告通知",
    tone: "blue",
    time: "2026/06/04 15:20",
    unread: false,
  },
  {
    id: "points-expire",
    icon: Medal,
    title: "积分即将过期提醒",
    summary: "您有 30 积分将于 7 天后过期，请及时使用。",
    detail: "过期积分将按系统规则自动失效。您可以先查看积分明细，再决定是否兑换或使用。",
    type: "points",
    typeLabel: "积分提醒",
    tone: "amber",
    time: "2026/06/03 10:00",
    unread: false,
  },
  {
    id: "login-device",
    icon: Smartphone,
    title: "新设备登录确认",
    summary: "检测到来自新设备的登录请求，请确认是否由您本人操作。",
    detail: "如果不是您本人登录，请立即修改密码并结束异常设备会话，以避免账户被继续访问。",
    type: "system",
    typeLabel: "系统通知",
    tone: "blue",
    time: "2026/06/02 09:15",
    unread: false,
  },
];

const ACCOUNT_CENTER_STATS = [
  { label: "账户积分", key: "credits", value: "0", unit: "分", action: "积分明细", icon: CircleDollarSign, tone: "gold" },
  { label: "邀请人数", value: "0", unit: "人", action: "查看详情", icon: UsersRound, tone: "violet" },
  { label: "累计生图", key: "generatedImageCount", value: "0", unit: "张", action: "生图明细", icon: ImagePlus, tone: "green" },
  { label: "账户等级", key: "level", value: "用户", action: "查看权益", icon: Gem, tone: "blue" },
];

const ACCOUNT_CENTER_QUICK_ACTIONS = [
  { title: "修改密码", text: "定期修改密码，保护账户安全", icon: LockKeyhole, action: { view: "settings", section: "password" } },
  { title: "充值中心", text: "多种充值方式，快速到账", icon: CircleDollarSign, action: { view: "points" } },
  { title: "我的积分", text: "积分兑换好礼，享受更多权益", icon: Star, action: { view: "points" } },
  { title: "邀请推广", text: "邀请好友注册，获取丰厚奖励", icon: Gift },
  { title: "安全设置", text: "绑定手机、邮箱等安全设置", icon: ShieldCheck, action: { view: "settings" } },
  { title: "消息通知", text: "查看系统消息和通知公告", icon: Bell, action: { view: "notifications" } },
];

const ACCOUNT_ANNOUNCEMENT_TYPES = new Set(["system", "announcement"]);

const ACCOUNT_POINTS_BANNER_STATS = [
  {
    icon: Gift,
    title: "积分获取",
    text: "兑换码可获得积分",
  },
  {
    icon: Star,
    title: "积分消耗",
    text: "对话、生图消耗积分",
  },
  {
    icon: Medal,
    title: "积分有效期",
    text: "部分积分将在获取后 180 天过期",
  },
];

const ACCOUNT_POINTS_PACKAGES = [
  {
    id: "points-pack-100",
    type: "amount",
    points: 800,
    label: "800 积分",
    note: "¥ 10.00",
    icon: Coins,
    tone: "blue",
  },
  {
    id: "points-pack-500",
    type: "amount",
    points: 4000,
    label: "4000 积分",
    note: "¥ 50.00",
    icon: Coins,
    tone: "violet",
  },
  {
    id: "points-pack-1000",
    type: "amount",
    points: 10000,
    label: "1,0000 积分",
    note: "¥ 100.00",
    icon: Coins,
    tone: "green",
  },
  {
    id: "points-pack-voucher",
    type: "voucher",
    label: "兑换券",
    note: "输入兑换码",
    icon: Gift,
    tone: "gold",
  },
  {
    id: "points-pack-custom",
    type: "custom",
    label: "自定义金额",
    note: "输入金额",
    icon: Sparkles,
    tone: "amber",
  },
];

const ACCOUNT_POINTS_PAYMENT_METHODS = [
  { value: "wechat", label: "微信支付", icon: MessageCircle, tone: "green" },
  { value: "alipay", label: "支付宝支付", icon: CircleDollarSign, tone: "blue" },
];

const ACCOUNT_POINTS_RECORD_FILTERS = [
  { value: "all", label: "全部" },
  { value: "income", label: "收入" },
  { value: "expense", label: "支出" },
];

const ACCOUNT_POINTS_RECORD_TYPE_FILTERS = [
  { value: "all", label: "全部类型" },
  { value: "image", label: "文生图" },
  { value: "chat", label: "对话" },
  { value: "invite", label: "邀请奖励" },
  { value: "redeem", label: "兑换礼品" },
  { value: "sign", label: "每日签到" },
  { value: "activity", label: "活动奖励" },
  { value: "voucher", label: "兑换券" },
  { value: "expire", label: "过期回收" },
  { value: "system", label: "系统补偿" },
];

const ACCOUNT_POINTS_RECORD_TOTAL = 24;
const ACCOUNT_POINTS_RECORD_ITEMS = [
  {
    id: "points-record-1",
    time: "2026/06/04 10:33:27",
    type: "image",
    typeLabel: "文生图",
    description: "文生图任务消耗",
    change: -200,
    balance: 9738,
    icon: ImagePlus,
    tone: "blue",
  },
  {
    id: "points-record-2",
    time: "2026/06/03 15:20:11",
    type: "invite",
    typeLabel: "邀请奖励",
    description: "邀请好友注册奖励",
    change: 100,
    balance: 9938,
    icon: UsersRound,
    tone: "green",
  },
  {
    id: "points-record-3",
    time: "2026/06/01 09:14:55",
    type: "redeem",
    typeLabel: "兑换礼品",
    description: "兑换礼品：无线充电器",
    change: -200,
    balance: 9738,
    icon: Gift,
    tone: "violet",
  },
  {
    id: "points-record-4",
    time: "2026/05/28 18:47:33",
    type: "sign",
    typeLabel: "每日签到",
    description: "每日签到奖励",
    change: 10,
    balance: 9748,
    icon: Check,
    tone: "green",
  },
  {
    id: "points-record-5",
    time: "2026/05/25 11:18:02",
    type: "chat",
    typeLabel: "对话",
    description: "高频对话消耗",
    change: -20,
    balance: 9728,
    icon: MessageCircle,
    tone: "blue",
  },
  {
    id: "points-record-6",
    time: "2026/05/22 20:09:41",
    type: "activity",
    typeLabel: "活动奖励",
    description: "周末活动积分奖励",
    change: 200,
    balance: 9928,
    icon: Sparkles,
    tone: "amber",
  },
  {
    id: "points-record-7",
    time: "2026/05/18 08:15:20",
    type: "expire",
    typeLabel: "过期回收",
    description: "过期积分自动回收",
    change: -30,
    balance: 9898,
    icon: Medal,
    tone: "orange",
  },
  {
    id: "points-record-8",
    time: "2026/05/15 13:52:08",
    type: "system",
    typeLabel: "系统补偿",
    description: "服务延迟补偿",
    change: 50,
    balance: 9948,
    icon: ShieldCheck,
    tone: "blue",
  },
  {
    id: "points-record-9",
    time: "2026/05/12 09:27:36",
    type: "image",
    typeLabel: "文生图",
    description: "高清生图任务消耗",
    change: -80,
    balance: 9868,
    icon: ImagePlus,
    tone: "blue",
  },
  {
    id: "points-record-10",
    time: "2026/05/08 17:44:05",
    type: "voucher",
    typeLabel: "兑换券",
    description: "兑换券充值到账",
    change: 500,
    balance: 10368,
    icon: Gift,
    tone: "gold",
  },
];

function getInitialSidebarCollapsed() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;
}

function isMobileSidebarViewport() {
  return typeof window !== "undefined"
    && Boolean(window.matchMedia?.(MOBILE_SIDEBAR_QUERY).matches);
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [settings, setSettings] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [images, setImages] = useState([]);
  const [imageCount, setImageCount] = useState("1");
  const [aspectRatio, setAspectRatio] = useState("auto");
  const [model, setModel] = useState("");
  const [advancedResolution, setAdvancedResolution] = useState("standard");
  const [advancedStyle, setAdvancedStyle] = useState("realistic");
  const [advancedQuality, setAdvancedQuality] = useState("high");
  const [advancedBackground, setAdvancedBackground] = useState("auto");
  const [advancedSteps, setAdvancedSteps] = useState("55");
  const [advancedSampler, setAdvancedSampler] = useState("k_lms");
  const [history, setHistory] = useState([]);
  const [historyScopeKey, setHistoryScopeKey] = useState("");
  const [activeId, setActiveId] = useState("");
  const [status, setStatus] = useState("idle");
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");
  const [authInfo, setAuthInfo] = useState(() => readStoredAuthStatus());
  const authInfoRef = useRef(authInfo);
  const [chatConversations, setChatConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState("");
  const [chatModel, setChatModel] = useState("");
  const [chatStatus, setChatStatus] = useState("idle");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  const [activeWorkspace, setActiveWorkspace] = useState("");
  const [galleryLightboxId, setGalleryLightboxId] = useState("");
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [announcementDialogItems, setAnnouncementDialogItems] = useState([]);
  const [announcementDialogPending, setAnnouncementDialogPending] = useState(false);
  const { dismissToast, pushToast, toasts } = useToastQueue();
  const fileInputRef = useRef(null);
  const promptTextareaRef = useRef(null);
  const abortRef = useRef(null);
  const chatAbortRef = useRef(null);
  const announcementPromptedScopeRef = useRef("");

  useLayoutEffect(() => {
    const nextTheme = theme === "dark" ? "dark" : "light";
    applyTheme(nextTheme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    } catch {
      // Ignore write failures in private or restricted contexts.
    }
  }, [theme]);

  useEffect(() => {
    let mounted = true;
    fetchPublicSettings().then((nextSettings) => {
      if (!mounted) {
        return;
      }
      setSettings(nextSettings);
      const models = normalizeModels(nextSettings);
      const defaultModel = models.some((item) => item.id === nextSettings.image_model)
        ? nextSettings.image_model
        : models[0]?.id || "";
      setModel((current) => (models.some((item) => item.id === current) ? current : defaultModel));
      const chatModels = normalizeChatModels(nextSettings);
      const defaultChatModel = chatModels.some((item) => item.id === nextSettings.chat_model)
        ? nextSettings.chat_model
        : chatModels[0]?.id || "";
      setChatModel((current) => (chatModels.some((item) => item.id === current) ? current : defaultChatModel));
    });
    return () => {
      mounted = false;
    };
  }, []);

  async function refreshAuthInfo(preferredAuthInfo = null) {
    if (preferredAuthInfo) {
      setAuthInfo(preferredAuthInfo);
      writeStoredAuthStatus(preferredAuthInfo);
    }
    try {
      const nextAuth = await fetchAuthStatus();
      const mergedAuth = mergePreferredAuthInfo(nextAuth, preferredAuthInfo);
      setAuthInfo(mergedAuth);
      writeStoredAuthStatus(mergedAuth);
      return mergedAuth;
    } catch {
      const fallbackAuth = preferredAuthInfo || authInfoRef.current || readStoredAuthStatus();
      if (fallbackAuth) {
        setAuthInfo(fallbackAuth);
        return fallbackAuth;
      }
      return null;
    }
  }

  useEffect(() => {
    refreshAuthInfo();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const collapseOnMobile = (event) => {
      if (event.matches) {
        setSidebarCollapsed(true);
      }
    };
    if (mediaQuery.matches) {
      setSidebarCollapsed(true);
    }
    mediaQuery.addEventListener?.("change", collapseOnMobile);
    return () => {
      mediaQuery.removeEventListener?.("change", collapseOnMobile);
    };
  }, []);

  const title = normalizeTitle(settings?.frontend_title);
  const models = useMemo(() => normalizeModels(settings || {}), [settings]);
  const modelOptions = useMemo(
    () => models.map((item) => ({ value: item.id, label: item.label || item.id, icon: ImagePlus })),
    [models],
  );
  const chatModels = useMemo(() => normalizeChatModels(settings || {}), [settings]);
  const chatModelOptions = useMemo(
    () => chatModels.map((item) => ({
      value: item.id,
      label: item.label || item.id,
      icon: MessageCircle,
    })),
    [chatModels],
  );
  const activeConversation = chatConversations.find((item) => item.id === activeConversationId) || null;
  const activeConversationModel = activeConversation?.model || "";
  const selectedChatModel = chatModels.some((item) => item.id === activeConversationModel)
    ? activeConversationModel
    : chatModel;
  useEffect(() => {
    setModel((current) => {
      if (models.some((item) => item.id === current)) {
        return current;
      }
      return models[0]?.id || "";
    });
  }, [models]);
  useEffect(() => {
    setChatModel((current) => {
      if (chatModels.some((item) => item.id === current)) {
        return current;
      }
      return chatModels[0]?.id || "";
    });
  }, [chatModels]);
  const resolutionOptions = useMemo(
    () => buildResolutionOptions(settings, model, aspectRatio),
    [aspectRatio, settings, model],
  );
  const aspectOptions = useMemo(
    () => buildAspectRatioOptions(settings, model, advancedResolution),
    [advancedResolution, settings, model],
  );
  const backgroundOptions = useMemo(() => buildBackgroundOptions(model), [model]);
  useEffect(() => {
    authInfoRef.current = authInfo;
  }, [authInfo]);

  const authScopeKey = useMemo(() => getAuthScopeKey(authInfo), [authInfo]);
  const isAuthenticated = Boolean(authInfo?.authenticated);
  const chatEnabled = settings?.frontend_chat_enabled !== false;
  const defaultWorkspace = chatEnabled ? "chat" : "image";
  const visibleWorkspace = activeWorkspace || defaultWorkspace;
  const visibleHistory = useMemo(
    () => (historyScopeKey === authScopeKey ? expandHistoryItems(history) : []),
    [authScopeKey, history, historyScopeKey],
  );
  const galleryLightboxItems = useMemo(
    () => [...visibleHistory].filter((item) => getDisplayImageSrc(item)).reverse(),
    [visibleHistory],
  );
  const requestedImageCount = Math.max(1, Math.min(Number(imageCount) || 1, 4));
  const unitCreditCost = getModelCreditCost(settings, model, advancedResolution);
  const creditCost = unitCreditCost * requestedImageCount;
  const canSpendCredits = authInfo?.authenticated
    ? Number(authInfo?.user?.credits || 0) >= creditCost
    : Boolean(authInfo?.guest?.allowed && Number(authInfo?.guest?.remaining || 0) > 0);
  const activeImage = visibleHistory.find((item) => item.id === activeId) || visibleHistory.at(-1) || null;
  const activeIndex = activeImage ? visibleHistory.findIndex((item) => item.id === activeImage.id) : -1;
  const galleryLightboxItem = galleryLightboxItems.find((item) => item.id === galleryLightboxId) || null;
  const galleryLightboxIndex = galleryLightboxItem
    ? galleryLightboxItems.findIndex((item) => item.id === galleryLightboxItem.id)
    : -1;
  const advancedControlsDisabled = normalizeResolutionValue(advancedResolution) === STANDARD_RESOLUTION;
  const canGenerate = (prompt.trim() || images.length > 0) && status !== "generating" && canSpendCredits;
  const canAddReferenceImages = images.length < MAX_REFERENCE_IMAGES;

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    const textarea = promptTextareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "auto";
    const maxHeight = Math.min(480, Math.max(176, Math.round(window.innerHeight * 0.45)));
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [prompt]);

  useEffect(() => {
    if (!isAuthenticated && activeWorkspace === "gallery") {
      setActiveWorkspace("image");
    }
  }, [activeWorkspace, isAuthenticated]);

  useEffect(() => {
    if (!chatEnabled && activeWorkspace === "chat") {
      setActiveWorkspace("image");
    }
  }, [activeWorkspace, chatEnabled]);

  useEffect(() => {
    if (!isAuthenticated) {
      setChatConversations([]);
      setActiveConversationId("");
      setRedeemDialogOpen(false);
      setAnnouncementDialogOpen(false);
      setAnnouncementDialogItems([]);
      setAnnouncementDialogPending(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authInfo?.authenticated || !authInfo?.user?.id) {
      announcementPromptedScopeRef.current = "";
      return undefined;
    }

    const scopeKey = getAuthScopeKey(authInfo);
    if (announcementPromptedScopeRef.current === scopeKey) {
      return undefined;
    }

    let cancelled = false;
    announcementPromptedScopeRef.current = scopeKey;
    (async () => {
      try {
        const snapshot = await fetchAccountNotifications({ limit: 200, unread: true });
        if (cancelled) {
          return;
        }
        const nextItems = normalizeAccountNotificationItems(snapshot?.items || [])
          .filter((item) => ACCOUNT_ANNOUNCEMENT_TYPES.has(item.type) && item.unread);
        setAnnouncementDialogItems(nextItems);
        setAnnouncementDialogOpen(nextItems.length > 0);
      } catch (error) {
        if (!cancelled) {
          console.warn("Failed to load announcement notifications", error);
          setAnnouncementDialogItems([]);
          setAnnouncementDialogOpen(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authInfo?.authenticated, authInfo?.user?.id]);

  useEffect(() => {
    if (aspectOptions.length > 0 && !aspectOptions.some((item) => item.value === aspectRatio)) {
      setAspectRatio(aspectOptions[0].value);
    }
  }, [aspectOptions, aspectRatio]);

  useEffect(() => {
    if (resolutionOptions.length > 0 && !resolutionOptions.some((item) => item.value === advancedResolution)) {
      setAdvancedResolution(resolutionOptions[0].value);
    }
  }, [advancedResolution, resolutionOptions]);

  useEffect(() => {
    if (backgroundOptions.length > 0 && !backgroundOptions.some((item) => item.value === advancedBackground)) {
      setAdvancedBackground(backgroundOptions[0].value);
    }
  }, [advancedBackground, backgroundOptions]);

  useEffect(() => {
    if (!authInfo) {
      setHistory([]);
      setHistoryScopeKey("");
      setActiveId("");
      return undefined;
    }
    let cancelled = false;
    setHistory([]);
    setHistoryScopeKey(authScopeKey);
    setActiveId("");
    fetchGenerationHistory(IMAGE_HISTORY_STRIP_LIMIT)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        const items = normalizeHistoryItems(payload?.items || []);
        setHistory(items);
        setHistoryScopeKey(authScopeKey);
        setActiveId(items.at(-1)?.id || "");
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn("Failed to load generation history", error);
          setHistory([]);
          setHistoryScopeKey(authScopeKey);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authInfo, authScopeKey]);

  useEffect(() => {
    if (visibleHistory.length === 0) {
      if (activeId) {
        setActiveId("");
      }
      if (galleryLightboxId) {
        setGalleryLightboxId("");
      }
      return;
    }
    if (!visibleHistory.some((item) => item.id === activeId)) {
      setActiveId(visibleHistory.at(-1)?.id || "");
    }
    if (galleryLightboxId && !galleryLightboxItems.some((item) => item.id === galleryLightboxId)) {
      setGalleryLightboxId("");
    }
  }, [activeId, galleryLightboxId, galleryLightboxItems, visibleHistory]);

  async function handleFiles(fileList) {
    const files = Array.from(fileList || [])
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, MAX_REFERENCE_IMAGES - images.length);
    if (files.length === 0) {
      return;
    }
    const nextImages = await Promise.all(files.map(readFileAsDataUrl));
    setImages((current) => [
      ...current,
      ...nextImages.map((src) => ({ id: createClientId(), src })),
    ]);
  }

  async function refreshGeneratedImageCache(taskId, payload, aspectOptions) {
    if (!taskId) {
      return;
    }
    for (const waitMs of [500, 1000, 2000]) {
      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      try {
        const refreshedResult = await fetchGenerationTaskResult(taskId);
        if (!refreshedResult) {
          continue;
        }
        const refreshedItem = createHistoryItem(taskId, refreshedResult, payload, aspectOptions);
        const hasCachedImage = Boolean(
          refreshedItem.cachedImageUrl
          || refreshedItem.images?.some((image) => image.cachedImageUrl || image.cached_image_url),
        );
        if (!hasCachedImage) {
          continue;
        }
        setHistory((current) => current.map((item) => (
          item.taskId === taskId || item.id === taskId
            ? { ...item, ...refreshedItem, createdAt: item.createdAt }
            : item
        )));
        return;
      } catch {
        return;
      }
    }
  }

  async function handleGenerate() {
    if (!canGenerate) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus("generating");
    setStatusText("");
    setError("");

    try {
      const payload = {
        prompt: prompt.trim(),
        inputImages: images.map((item) => item.src),
        aspectRatio,
        imageCount: requestedImageCount,
        model,
        timestamp: Date.now(),
        visitorId: getVisitorId(),
      };
      Object.assign(
        payload,
        buildAdvancedGeneratePayload({
          resolution: advancedResolution,
          negativePrompt,
          style: advancedStyle,
          quality: advancedQuality,
          background: advancedBackground,
          model,
          steps: advancedSteps,
          sampler: advancedSampler,
        }),
      );
      if (payload.inputImages.length > 0) {
        setImages([]);
      }
      let renderedId = "";
      const result = await generateImageStream(payload, {
        signal: controller.signal,
        onStatus: (nextStatus) => {
          setStatusText(statusLabel(nextStatus));
        },
        onResult: (streamResult) => {
          renderedId = streamResult.taskId || createClientId();
          const item = createHistoryItem(renderedId, streamResult, payload, aspectOptions);
          setHistoryScopeKey(authScopeKey);
          setHistory((current) => [...current, item].slice(-IMAGE_HISTORY_STRIP_LIMIT));
          setActiveId(item.id);
          setStatus("complete");
          setStatusText(GENERATE_PROGRESS.completed);
          refreshAuthInfo();
          if (!streamResult.cachedImageUrl && !streamResult.images?.some((image) => image.cachedImageUrl || image.cached_image_url)) {
            void refreshGeneratedImageCache(renderedId, payload, aspectOptions);
          }
        },
        onCache: (cacheResult) => {
          const cacheId = cacheResult.taskId || renderedId;
          if (!cacheId || !cacheResult.cachedImageUrl) {
            return;
          }
          setHistory((current) => current.map((item) => (
            item.taskId === cacheId || item.id === cacheId
              ? {
                ...item,
                cachedImageUrl: cacheResult.cachedImageUrl,
                thumbnailUrl: thumbnailUrlForDisplay(cacheResult.cachedImageUrl) || item.thumbnailUrl,
              }
              : item
          )));
        },
      });
      if (!renderedId) {
        const item = createHistoryItem(result.taskId || createClientId(), result, payload, aspectOptions);
        setHistoryScopeKey(authScopeKey);
        setHistory((current) => [...current, item].slice(-IMAGE_HISTORY_STRIP_LIMIT));
        setActiveId(item.id);
        setStatus("complete");
        setStatusText(GENERATE_PROGRESS.completed);
        refreshAuthInfo();
        if (!result.cachedImageUrl && !result.images?.some((image) => image.cachedImageUrl || image.cached_image_url)) {
          void refreshGeneratedImageCache(item.taskId, payload, aspectOptions);
        }
      }
    } catch (nextError) {
      if (nextError?.name === "AbortError") {
        setStatus("idle");
        setStatusText("");
        return;
      }
      setError(nextError instanceof Error ? nextError.message : "生成失败");
      setStatus("failed");
      setStatusText(GENERATE_PROGRESS.failed);
    } finally {
      abortRef.current = null;
    }
  }

  async function removeHistoryItem(id) {
    const target = visibleHistory.find((item) => item.id === id);
    if (!target) {
      return;
    }
    const taskId = target.taskId || target.task_id || target.sourceId || target.source_id || target.id;
    const rawTarget = history.find((item) => (item.taskId || item.task_id || item.id) === taskId) || target;
    try {
      await deleteGenerationHistoryItem(taskId);
      await purgeCachedHistoryItem(rawTarget);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "删除历史记录失败");
      return;
    }
    setHistory((current) => {
      const next = current.filter((item) => (item.taskId || item.task_id || item.id) !== taskId);
      if (activeId === id || activeId === taskId) {
        setActiveId("");
      }
      return next;
    });
  }

  function navigateHistory(direction) {
    if (visibleHistory.length === 0 || activeIndex === -1) {
      return;
    }
    const nextIndex = direction === "prev" ? activeIndex - 1 : activeIndex + 1;
    if (nextIndex >= 0 && nextIndex < visibleHistory.length) {
      setActiveId(visibleHistory[nextIndex].id);
    }
  }

  function navigateGalleryLightbox(direction) {
    if (galleryLightboxItems.length === 0 || galleryLightboxIndex === -1) {
      return;
    }
    const nextIndex = direction === "prev" ? galleryLightboxIndex - 1 : galleryLightboxIndex + 1;
    if (nextIndex >= 0 && nextIndex < galleryLightboxItems.length) {
      setGalleryLightboxId(galleryLightboxItems[nextIndex].id);
    }
  }

  function collapseSidebar() {
    setSidebarCollapsed(true);
  }

  function openAuthDialog(mode = "login") {
    setAuthMode(mode);
    setAuthDialogOpen(true);
  }

  function openRedeemDialog() {
    if (!isAuthenticated) {
      openAuthDialog("login");
      return;
    }
    setRedeemDialogOpen(true);
  }

  async function handleAnnouncementRead() {
    const notificationIds = Array.from(new Set(
      announcementDialogItems
        .map((item) => Number(item.id))
        .filter((value) => Number.isFinite(value) && value > 0),
    ));
    if (!notificationIds.length) {
      setAnnouncementDialogOpen(false);
      setAnnouncementDialogItems([]);
      return;
    }

    setAnnouncementDialogPending(true);
    try {
      await markAccountNotificationRead(notificationIds);
      setAnnouncementDialogOpen(false);
      setAnnouncementDialogItems([]);
    } catch (error) {
      pushToast({
        type: "error",
        title: "公告已读失败",
        message: error instanceof Error ? error.message : "操作失败",
      });
    } finally {
      setAnnouncementDialogPending(false);
    }
  }

  function showChatUnavailableToast() {
    pushToast({
      type: "warning",
      title: CHAT_UNAVAILABLE_MESSAGE,
    });
  }

  function activateChatWorkspace() {
    if (!chatEnabled) {
      showChatUnavailableToast();
      return false;
    }
    setActiveWorkspace("chat");
    return true;
  }

  function navigateWorkspace(workspaceId) {
    if (workspaceId === "chat") {
      const activated = activateChatWorkspace();
      if (activated && isMobileSidebarViewport()) {
        setSidebarCollapsed(true);
      }
      return;
    }
    setActiveWorkspace(workspaceId);
    if (isMobileSidebarViewport()) {
      setSidebarCollapsed(true);
    }
  }

  function startNewConversation() {
    if (!activateChatWorkspace()) {
      return;
    }
    setActiveConversationId("");
    if (isMobileSidebarViewport()) {
      setSidebarCollapsed(true);
    }
  }

  function selectConversation(conversationId) {
    if (!activateChatWorkspace()) {
      return;
    }
    const nextConversation = chatConversations.find((item) => item.id === conversationId);
    if (nextConversation?.model && chatModels.some((item) => item.id === nextConversation.model)) {
      setChatModel(nextConversation.model);
    }
    setActiveConversationId(conversationId);
    if (isMobileSidebarViewport()) {
      setSidebarCollapsed(true);
    }
  }

  function deleteConversation(conversationId) {
    setChatConversations((current) => current.filter((item) => item.id !== conversationId));
    if (activeConversationId === conversationId) {
      setActiveConversationId("");
    }
  }

  function changeChatModel(nextModel) {
    const nextValue = String(nextModel || "").trim();
    if (!nextValue || !chatModels.some((item) => item.id === nextValue)) {
      return;
    }
    setChatModel(nextValue);
    if (!activeConversationId) {
      return;
    }
    setChatConversations((current) => current.map((item) => (
      item.id === activeConversationId
        ? { ...item, model: nextValue }
      : item
    )));
  }

  function deleteChatMessage(conversationId, messageId) {
    setChatConversations((current) => current.map((item) => (
      item.id === conversationId
        ? {
          ...item,
          updatedAt: Date.now(),
          messages: (item.messages || []).filter((message) => message.id !== messageId),
        }
        : item
    )));
  }

  function stopChatCompletion() {
    chatAbortRef.current?.abort();
    chatAbortRef.current = null;
    setChatStatus("idle");
    setChatConversations((current) => current.map((item) => ({
      ...item,
      messages: (item.messages || []).map((message) => (
        message.pending
          ? {
            ...message,
            pending: false,
            stopped: true,
            content: message.content || "已停止生成",
            retryNotice: "",
          }
          : message
      )),
    })));
  }

  function submitConversationMessage(content, attachments = [], titleOverride = "") {
    const text = String(content || "").trim();
    const readyAttachments = (attachments || []).filter((item) => item?.status === "ready" && item?.url);
    const targetConversationId = activeConversationId || createClientId();
    const existingConversation = chatConversations.find((item) => item.id === targetConversationId);
    const conversationModel = existingConversation?.model || selectedChatModel;
    if ((!text && readyAttachments.length === 0) || chatStatus === "sending" || !conversationModel) {
      return;
    }
    const now = Date.now();
    const fallbackTitle = text ? conversationTitleFromMessage(text) : "附件消息";
    const submittedTitle = normalizeConversationTitle(titleOverride) || fallbackTitle;
    const userRequestContent = buildChatRequestContent(text, readyAttachments);
    const message = {
      id: createClientId(),
      role: "user",
      content: text,
      attachments: readyAttachments,
      createdAt: now,
    };
    const pendingMessageId = createClientId();
    const pendingMessage = {
      id: pendingMessageId,
      role: "assistant",
      content: "",
      reasoning: "",
      retryNotice: "",
      artifacts: [],
      pending: true,
      createdAt: now + 1,
      startedAt: now,
      model: conversationModel,
    };
    const requestMessages = [
      ...((existingConversation?.messages || [])
        .filter((item) => item.role === "user" || item.role === "assistant")
        .filter((item) => !item.pending && !item.error)
        .map((item) => ({
          role: item.role,
          content: item.role === "user" ? buildChatRequestContent(item.content, item.attachments || []) : item.content,
        }))),
      { role: "user", content: userRequestContent },
    ];
    setActiveConversationId(targetConversationId);
    setChatStatus("sending");
    setChatConversations((current) => {
      const existing = current.find((item) => item.id === targetConversationId);
      if (!existing) {
        const nextConversation = {
          id: targetConversationId,
          title: submittedTitle,
          model: conversationModel,
          createdAt: now,
          updatedAt: now,
          messages: [message, pendingMessage],
        };
        return [nextConversation, ...current];
      }
      return current.map((item) => (
        item.id === existing.id
          ? {
            ...item,
            title: item.title || submittedTitle,
            model: item.model || conversationModel,
            updatedAt: now,
            messages: [...(item.messages || []), message, pendingMessage],
          }
          : item
      ));
    });
    chatAbortRef.current?.abort();
    const controller = new AbortController();
    chatAbortRef.current = controller;
    let streamedContent = "";
    let streamedReasoning = "";
    void (async () => {
      try {
      const result = await sendChatCompletion({
        model: conversationModel,
        messages: requestMessages,
        visitorId: getVisitorId(),
      }, {
        preferStream: true,
        signal: controller.signal,
        onDelta: (delta) => {
          streamedContent += delta;
          setChatConversations((current) => current.map((item) => (
            item.id === targetConversationId
              ? {
                ...item,
                updatedAt: Date.now(),
                messages: (item.messages || []).map((currentMessage) => (
                  currentMessage.id === pendingMessageId
                    ? { ...currentMessage, content: streamedContent }
                    : currentMessage
                )),
              }
              : item
          )));
        },
        onReasoningDelta: (delta) => {
          streamedReasoning += delta;
          setChatConversations((current) => current.map((item) => (
            item.id === targetConversationId
              ? {
                ...item,
                updatedAt: Date.now(),
                messages: (item.messages || []).map((currentMessage) => (
                  currentMessage.id === pendingMessageId
                    ? { ...currentMessage, reasoning: streamedReasoning, retryNotice: "" }
                    : currentMessage
                )),
              }
              : item
          )));
        },
        onRetry: (event) => {
          const retryNotice = chatRetryNoticeFromEvent(event);
          setChatConversations((current) => current.map((item) => (
            item.id === targetConversationId
              ? {
                ...item,
                updatedAt: Date.now(),
                messages: (item.messages || []).map((currentMessage) => (
                  currentMessage.id === pendingMessageId
                    ? { ...currentMessage, retryNotice }
                    : currentMessage
                )),
              }
              : item
          )));
        },
      });
      const completedAt = Date.now();
      const finalContent = String(result.message || streamedContent || "");
      const finalReasoning = String(
        result.reasoning || result.reasoningContent || result.reasoning_content || streamedReasoning || "",
      );
      if (!finalContent.trim() && !finalReasoning.trim()) {
        throw new Error("上游完成但没有返回任何可显示内容");
      }
      const assistantMessage = {
        id: pendingMessageId,
        role: "assistant",
        content: finalContent,
        reasoning: finalReasoning,
        artifacts: normalizeChatArtifacts(result.artifacts),
        pending: false,
        createdAt: completedAt,
        model: result.model || conversationModel,
        providerId: result.providerId || "",
        providerLabel: result.providerLabel || "",
        retryNotice: "",
      };
      setChatConversations((current) => current.map((item) => (
        item.id === targetConversationId
          ? {
            ...item,
            updatedAt: completedAt,
            messages: (item.messages || []).map((currentMessage) => (
              currentMessage.id === pendingMessageId ? assistantMessage : currentMessage
            )),
          }
          : item
      )));
      refreshAuthInfo();
    } catch (error) {
      if (error?.name === "AbortError") {
        setChatConversations((current) => current.map((item) => (
          item.id === targetConversationId
            ? {
              ...item,
              updatedAt: Date.now(),
              messages: (item.messages || []).map((currentMessage) => (
                currentMessage.id === pendingMessageId
                  ? {
                    ...currentMessage,
                    pending: false,
                    stopped: true,
                    content: currentMessage.content || streamedContent || "已停止生成",
                    reasoning: chatReasoningToText(currentMessage) || streamedReasoning,
                    retryNotice: "",
                  }
                  : currentMessage
              )),
            }
            : item
        )));
        return;
      }
      const messageText = error instanceof Error ? error.message : "对话请求失败";
      setChatConversations((current) => current.map((item) => (
        item.id === targetConversationId
          ? {
            ...item,
            updatedAt: Date.now(),
            messages: (item.messages || []).map((currentMessage) => (
              currentMessage.id === pendingMessageId
                ? {
                  ...currentMessage,
                  pending: false,
                  error: true,
                  content: messageText,
                  reasoning: chatReasoningToText(currentMessage) || streamedReasoning,
                  retryNotice: "",
                }
                : currentMessage
            )),
          }
          : item
      )));
      } finally {
        if (chatAbortRef.current === controller) {
          chatAbortRef.current = null;
        }
        setChatStatus("idle");
      }
    })();

    return {
      conversationId: targetConversationId,
      userMessageId: message.id,
      assistantMessageId: pendingMessageId,
    };
  }

  return (
    <main className={sidebarCollapsed ? "app-shell sidebar-collapsed" : "app-shell"}>
      <div className="app-body">
        <AppSidebar
          title={title}
          logoSvg={settings?.frontend_logo_svg}
          logoUrl={settings?.frontend_logo_url}
          authInfo={authInfo}
          activeWorkspace={activeWorkspace}
          conversations={chatConversations}
          activeConversationId={activeConversationId}
          sidebarCollapsed={sidebarCollapsed}
          theme={theme}
          onNavigate={navigateWorkspace}
          onNewConversation={startNewConversation}
          onSelectConversation={selectConversation}
          onAuthRequest={openAuthDialog}
          onDeleteConversation={deleteConversation}
          onSidebarToggle={() => setSidebarCollapsed((current) => !current)}
          onThemeToggle={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
        <button
          className={sidebarCollapsed ? "mobile-sidebar-toggle" : "mobile-sidebar-toggle is-hidden"}
          type="button"
          aria-label="打开侧边栏"
          onClick={() => setSidebarCollapsed(false)}
        >
          <List size={20} aria-hidden="true" />
        </button>
        <section
          className="workspace"
          onFocusCapture={collapseSidebar}
          onPointerDownCapture={collapseSidebar}
        >
          {visibleWorkspace === "gallery" ? (
            <GalleryWorkspace
              history={visibleHistory}
              onOpen={setGalleryLightboxId}
            />
          ) : visibleWorkspace === "chat" ? (
            <ChatWorkspace
              conversations={chatConversations}
              activeConversationId={activeConversationId}
              model={selectedChatModel}
              modelOptions={chatModelOptions}
              isSending={chatStatus === "sending"}
              onModelChange={changeChatModel}
              onDeleteMessage={deleteChatMessage}
              onStop={stopChatCompletion}
              onSubmit={submitConversationMessage}
            />
          ) : (
            <>
        <aside className="composer-panel">
          <label className="field-label" htmlFor="prompt">
            Prompt
          </label>
          <textarea
            id="prompt"
            ref={promptTextareaRef}
            className="prompt-textarea"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="描述你想生成的画面，或者先上传参考图。"
            spellCheck={false}
          />

          <div className="upload-card">
            <input
              ref={fileInputRef}
              className="sr-only"
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => {
                handleFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <div
              className={images.length > 0 ? "reference-strip has-items" : "reference-strip"}
              onDrop={(event) => {
                event.preventDefault();
                handleFiles(event.dataTransfer.files);
              }}
              onDragOver={(event) => event.preventDefault()}
            >
              {images.map((image, index) => (
                <div className="thumb" key={image.id}>
                  <img src={image.src} alt={`参考图 ${index + 1}`} />
                  <span
                    className="subthumb-del"
                    onClick={() => setImages((current) => current.filter((item) => item.id !== image.id))}
                    title="移除参考图"
                    aria-label={`移除参考图 ${index + 1}`}
                  >
                    <X size={14} />
                  </span>
                </div>
              ))}
              {canAddReferenceImages && (
                <button
                  className={images.length > 0 ? "upload-zone compact" : "upload-zone"}
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="上传参考图"
                >
                  <UploadCloud size={images.length > 0 ? 18 : 22} />
                  <span>上传参考图</span>
                </button>
              )}
            </div>
          </div>

          <div className="control-grid">
            <ModelField label="模型" value={model} options={modelOptions} onChange={setModel} />
            <AspectRatioField
              label="宽高比"
              value={aspectRatio}
              options={aspectOptions}
              onChange={setAspectRatio}
            />
          </div>

          <details className="advanced-settings">
            <summary className="advanced-settings-summary">
              <span className="field-label">高级设置</span>
              <ChevronDown className="advanced-settings-summary-icon" size={16} aria-hidden="true" />
            </summary>
            <div className="advanced-settings-panel">
              <div className="advanced-negative-prompt">
                <div className="advanced-negative-heading">
                  <label className="field-label" htmlFor="negative_prompt">
                    Negative_prompt
                  </label>
                  <span
                    className="advanced-help"
                    tabIndex={0}
                    aria-describedby="advanced-settings-help"
                    aria-label="高级设置参数说明"
                  >
                    <CircleHelp size={16} aria-hidden="true" />
                    <span className="advanced-help-tooltip" id="advanced-settings-help" role="tooltip">
                      {ADVANCED_HELP_ITEMS.map((item) => (
                        <span className="advanced-help-row" key={item.label}>
                          <strong>{item.label}：</strong>
                          <span>{item.text}</span>
                        </span>
                      ))}
                    </span>
                  </span>
                </div>
                <textarea
                  id="negative_prompt"
                  className="negative-prompt-textarea"
                  value={negativePrompt}
                  onChange={(event) => setNegativePrompt(event.target.value)}
                  placeholder="输入需要排除的元素，如文字、水印、模糊。"
                  spellCheck={false}
                  disabled={advancedControlsDisabled}
                />
              </div>
              <ListboxField
                label="分辨率"
                value={advancedResolution}
                options={resolutionOptions}
                onChange={setAdvancedResolution}
                showMeta
              />
              <ListboxField
                label="数量"
                value={imageCount}
                options={IMAGE_COUNT_OPTIONS}
                onChange={setImageCount}
              />
              <ListboxField
                label="风格"
                value={advancedStyle}
                options={ADVANCED_STYLE_OPTIONS}
                onChange={setAdvancedStyle}
                disabled={advancedControlsDisabled}
              />
              <ListboxField
                label="图片质量"
                value={advancedQuality}
                options={ADVANCED_QUALITY_OPTIONS}
                onChange={setAdvancedQuality}
                disabled={advancedControlsDisabled}
              />
              <ListboxField
                label="背景类型"
                value={advancedBackground}
                options={backgroundOptions}
                onChange={setAdvancedBackground}
                disabled={advancedControlsDisabled}
              />
              <ListboxField
                label="迭代步数"
                value={advancedSteps}
                options={ADVANCED_STEP_OPTIONS}
                onChange={setAdvancedSteps}
                disabled={advancedControlsDisabled}
              />
              <ListboxField
                label="采样算法"
                value={advancedSampler}
                options={ADVANCED_SAMPLER_OPTIONS}
                onChange={setAdvancedSampler}
                disabled={advancedControlsDisabled}
              />
            </div>
          </details>

          {error && <div className="error-box">{error}</div>}
          <button className="generate-button" type="button" disabled={!canGenerate} onClick={handleGenerate}>
            {status === "generating" ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
            <span>{status === "generating" ? statusText || "生成中" : `Generate Image · ${creditCost} 积分`}</span>
          </button>
        </aside>

        <section className="preview-panel">
          <div className="preview-stage">
            {status === "generating" ? (
              <LoadingState progress={statusText} />
            ) : activeImage ? (
              <ResultPreview
                item={activeImage}
                currentIndex={activeIndex}
                historyLength={visibleHistory.length}
                onNavigate={navigateHistory}
                onDelete={() => removeHistoryItem(activeImage.id)}
              />
            ) : (
              <EmptyState />
            )}
          </div>

          <HistoryStrip
            history={visibleHistory}
            activeId={activeImage?.id || ""}
            onSelect={setActiveId}
            onRemove={removeHistoryItem}
          />
        </section>
          </>
        )}
        </section>
      </div>
      <SponsoredBar settings={settings} />
      {galleryLightboxItem ? createPortal(
        <GalleryLightbox
          item={galleryLightboxItem}
          hasPrev={galleryLightboxIndex > 0}
          hasNext={galleryLightboxIndex >= 0 && galleryLightboxIndex < galleryLightboxItems.length - 1}
          onClose={() => setGalleryLightboxId("")}
          onNavigate={navigateGalleryLightbox}
        />,
        document.body,
      ) : null}
      {authDialogOpen ? createPortal(
        <AuthDialog
          mode={authMode}
          onModeChange={setAuthMode}
          settings={settings}
          authInfo={authInfo}
          onClose={() => setAuthDialogOpen(false)}
          onAuthenticated={refreshAuthInfo}
          pushToast={pushToast}
        />,
        document.body,
      ) : null}
      {redeemDialogOpen ? createPortal(
        <RedeemDialog
          authInfo={authInfo}
          onClose={() => setRedeemDialogOpen(false)}
          onAuthenticated={refreshAuthInfo}
        />,
        document.body,
      ) : null}
      {announcementDialogOpen ? createPortal(
        <AnnouncementDialog
          items={announcementDialogItems}
          isPending={announcementDialogPending}
          onClose={() => setAnnouncementDialogOpen(false)}
          onMarkRead={handleAnnouncementRead}
        />,
        document.body,
      ) : null}
    </main>
  );
}

function AuthDialog({ mode, onModeChange, settings, authInfo, onClose, onAuthenticated, pushToast }) {
  const [loginError, setLoginError] = useState("");
  const [loginPending, setLoginPending] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const closeButtonRef = useRef(null);
  const user = authInfo?.user || null;
  const effectiveMode = user ? "account" : mode;

  useEffect(() => {
    closeButtonRef.current?.focus();
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function handleLoginSubmit(event) {
    event.preventDefault();
    setLoginError("");
    setLoginPending(true);
    try {
      const data = Object.fromEntries(new FormData(event.currentTarget));
      const result = await loginUser(data.username, data.password, Boolean(data.remember));
      await onAuthenticated(authInfoWithUser(authInfo, result?.user));
      onClose();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "登录请求失败");
    } finally {
      setLoginPending(false);
    }
  }

  async function handleRegisterSubmit(event) {
    event.preventDefault();
    setLoginError("");
    setLoginPending(true);
    try {
      const data = Object.fromEntries(new FormData(event.currentTarget));
      const result = await registerUser(data.username, data.email || null, data.password);
      await onAuthenticated(authInfoWithUser(authInfo, result?.user));
      onModeChange("account");
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "注册失败");
    } finally {
      setLoginPending(false);
    }
  }

  async function handleLogout(event) {
    event.preventDefault();
    event.stopPropagation();
    setLoginError("");
    setLogoutPending(true);
    try {
      await logoutUser();
      await onAuthenticated(authInfoLoggedOut(authInfo));
      onModeChange("login");
      onClose();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "退出登录失败");
    } finally {
      setLogoutPending(false);
    }
  }

  const registrationEnabled = settings?.allow_user_registration !== false;
  const isAccountCenter = effectiveMode === "account" && user;

  return (
    <div className="auth-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className={isAccountCenter ? "auth-dialog account-center-dialog" : "auth-dialog"}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-dialog-title"
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          className={isAccountCenter ? "auth-close account-center-close" : "auth-close"}
          type="button"
          aria-label={user ? "关闭用户中心" : "关闭登录窗口"}
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>

        {isAccountCenter ? (
          <AccountCenterPanel
            authInfo={authInfo}
            loginError={loginError}
            logoutPending={logoutPending}
            onAuthenticated={onAuthenticated}
            onLogout={handleLogout}
            pushToast={pushToast}
            user={user}
          />
        ) : (
          <>
            <div className="auth-heading">
              <div className="auth-mark">
                {effectiveMode === "login" ? <LogIn size={20} aria-hidden="true" /> : <UserPlus size={20} aria-hidden="true" />}
              </div>
              <div>
                <h2 id="auth-dialog-title">{effectiveMode === "login" ? "登录账户" : "创建账户"}</h2>
                <p>{effectiveMode === "login" ? "进入账户" : "创建新账户"}</p>
              </div>
            </div>

            <div className="auth-tabs" role="tablist" aria-label="账户操作">
              <button
                className={effectiveMode === "login" ? "is-active" : ""}
                type="button"
                role="tab"
                aria-selected={effectiveMode === "login"}
                onClick={() => onModeChange("login")}
              >
                登录
              </button>
              <button
                className={effectiveMode === "register" ? "is-active" : ""}
                type="button"
                role="tab"
                aria-selected={effectiveMode === "register"}
                onClick={() => onModeChange("register")}
              >
                注册
              </button>
            </div>

            {effectiveMode === "login" ? (
              <form className="auth-form" onSubmit={handleLoginSubmit}>
                <label>
                  <span>账号</span>
                  <input name="username" type="text" autoComplete="username" required />
                </label>
                <label>
                  <span>密码</span>
                  <input name="password" type="password" autoComplete="current-password" required />
                </label>
                <label className="auth-remember">
                  <input name="remember" type="checkbox" value="1" />
                  <span>记住登录 30 天</span>
                </label>
                {loginError ? <div className="auth-error">{loginError}</div> : null}
                <button className="auth-submit" type="submit" disabled={loginPending}>
                  {loginPending ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <LogIn size={17} aria-hidden="true" />}
                  <span>{loginPending ? "正在登录" : "登录"}</span>
                </button>
              </form>
            ) : registrationEnabled ? (
              <form className="auth-form" onSubmit={handleRegisterSubmit}>
                <label>
                  <span>账号</span>
                  <input name="username" type="text" autoComplete="username" required />
                </label>
                <label>
                  <span>邮箱</span>
                  <input name="email" type="email" autoComplete="email" />
                </label>
                <label>
                  <span>密码</span>
                  <input name="password" type="password" autoComplete="new-password" required />
                </label>
                {loginError ? <div className="auth-error">{loginError}</div> : null}
                <button className="auth-submit" type="submit" disabled={loginPending}>
                  {loginPending ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <UserPlus size={17} aria-hidden="true" />}
                  <span>{loginPending ? "正在注册" : "注册"}</span>
                </button>
              </form>
            ) : (
              <div className="auth-error">注册暂未开放</div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function AccountCenterPanel({ authInfo, loginError, logoutPending, onAuthenticated, onLogout, pushToast, user }) {
  const displayName = user?.username || "当前用户";
  const currentEmail = normalizeOptionalText(user?.email);
  const currentPhone = normalizeOptionalText(user?.phone || user?.phone_number || user?.phoneNumber);
  const displayEmail = currentEmail || "未设置邮箱";
  const displayPhone = currentPhone || "未绑定手机号";
  const displayCredits = formatAccountNumber(user?.credits);
  const displayGeneratedCount = formatAccountNumber(
    firstFiniteNumber(
      user?.usage?.generation_count,
      user?.usage?.generationCount,
      user?.usage?.generated_image_count,
      user?.usage?.generatedImageCount,
      user?.generation_count,
      user?.generationCount,
      user?.generated_image_count,
      user?.generatedImageCount,
    ),
  );
  const createdAt = formatAccountDateTime(user?.created_at || user?.createdAt);
  const lastLoginAt = formatAccountDateTime(user?.last_login_at || user?.lastLoginAt);
  const isAdmin = Boolean(user?.is_admin || user?.role === "admin");
  const roleLabel = formatAccountRole(user);
  const accountStats = ACCOUNT_CENTER_STATS.map((item) => {
    if (item.key === "credits") {
      return { ...item, value: displayCredits };
    }
    if (item.key === "level") {
      return { ...item, value: roleLabel };
    }
    if (item.key === "generatedImageCount") {
      return { ...item, value: displayGeneratedCount };
    }
    return item;
  });
  const [activeView, setActiveView] = useState(ACCOUNT_CENTER_DEFAULT_VIEW);
  const [notifications, setNotifications] = useState([]);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [notificationTypeFilter, setNotificationTypeFilter] = useState("all");
  const [selectedNotificationIds, setSelectedNotificationIds] = useState(() => new Set());
  const [expandedNotificationId, setExpandedNotificationId] = useState(null);
  const [ledgerItems, setLedgerItems] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(null);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [settingsAction, setSettingsAction] = useState("");
  const [settingsExpandedItem, setSettingsExpandedItem] = useState("");
  const accountCenterContentRef = useRef(null);
  const selectedNotificationCount = selectedNotificationIds.size;
  const visibleNotifications = useMemo(() => {
    return notifications.filter((item) => {
      if (notificationFilter === "unread" && !item.unread) {
        return false;
      }
      if (notificationFilter === "read" && item.unread) {
        return false;
      }
      if (notificationTypeFilter !== "all" && item.type !== notificationTypeFilter) {
        return false;
      }
      return true;
    });
  }, [notifications, notificationFilter, notificationTypeFilter]);

  function applyNotificationSnapshot(snapshot) {
    const nextItems = normalizeAccountNotificationItems(snapshot?.items || []);
    const nextUnreadCount = firstFiniteNumber(snapshot?.unread_count, snapshot?.unreadCount, nextItems.filter((item) => item.unread).length);
    setNotifications(nextItems);
    setNotificationTotal(firstFiniteNumber(snapshot?.total, nextItems.length));
    setNotificationUnreadCount(nextUnreadCount);
    setSelectedNotificationIds((current) => {
      const nextIds = new Set(nextItems.map((item) => item.id));
      const next = new Set();
      current.forEach((notificationId) => {
        if (nextIds.has(notificationId)) {
          next.add(notificationId);
        }
      });
      return next;
    });
    setExpandedNotificationId((current) => (nextItems.some((item) => item.id === current) ? current : null));
  }

  function applyLedgerSnapshot(snapshot) {
    const nextItems = normalizeAccountLedgerItems(snapshot?.items || []);
    setLedgerItems(nextItems);
    setLedgerBalance(firstFiniteNumber(snapshot?.balance, snapshot?.balance_after, user?.credits, authInfo?.user?.credits, 0));
    setLedgerTotal(firstFiniteNumber(snapshot?.total, nextItems.length));
  }

  async function reloadNotificationState() {
    const snapshot = await fetchAccountNotifications({ limit: 200 });
    applyNotificationSnapshot(snapshot);
    return snapshot;
  }

  async function reloadLedgerState() {
    const snapshot = await fetchAccountCreditsLedger(50);
    applyLedgerSnapshot(snapshot);
    return snapshot;
  }

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setNotifications([]);
      setNotificationTotal(0);
      setNotificationUnreadCount(0);
      setNotificationsLoading(false);
      setSelectedNotificationIds(new Set());
      setExpandedNotificationId(null);
      setLedgerItems([]);
      setLedgerBalance(null);
      setLedgerTotal(0);
      setLedgerLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setNotificationsLoading(true);
    setLedgerLoading(true);
    setSelectedNotificationIds(new Set());
    setExpandedNotificationId(null);
    (async () => {
      try {
        const snapshot = await fetchAccountNotifications({ limit: 200 });
        if (!cancelled) {
          applyNotificationSnapshot(snapshot);
        }
      } catch {
        if (!cancelled) {
          setNotifications([]);
          setNotificationTotal(0);
          setNotificationUnreadCount(0);
        }
      } finally {
        if (!cancelled) {
          setNotificationsLoading(false);
        }
      }
    })();
    (async () => {
      try {
        const snapshot = await fetchAccountCreditsLedger(50);
        if (!cancelled) {
          applyLedgerSnapshot(snapshot);
        }
      } catch {
        if (!cancelled) {
          setLedgerItems([]);
          setLedgerBalance(firstFiniteNumber(user?.credits, authInfo?.user?.credits, 0));
          setLedgerTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLedgerLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authInfo?.user?.credits, user?.id]);

  useEffect(() => {
    accountCenterContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [activeView]);

  async function markNotificationAsRead(notificationId) {
    const targetNotification = notifications.find((item) => item.id === notificationId);
    if (!targetNotification || !targetNotification.unread) {
      return;
    }
    const previousNotifications = notifications;
    const previousUnreadCount = notificationUnreadCount;
    const previousSelectedIds = new Set(selectedNotificationIds);
    const previousExpandedId = expandedNotificationId;
    setNotifications((current) => current.map((item) => (item.id === notificationId ? { ...item, unread: false } : item)));
    setNotificationUnreadCount((current) => Math.max(0, current - 1));
    try {
      const snapshot = await markAccountNotificationRead(notificationId);
      applyNotificationSnapshot(snapshot);
    } catch (error) {
      setNotifications(previousNotifications);
      setNotificationUnreadCount(previousUnreadCount);
      setSelectedNotificationIds(previousSelectedIds);
      setExpandedNotificationId(previousExpandedId);
      pushToast?.({
        type: "error",
        title: "\u6807\u8BB0\u5DF2\u8BFB\u5931\u8D25",
        message: error instanceof Error ? error.message : "\u64CD\u4F5C\u5931\u8D25",
      });
    }
  }

  async function handleMarkAllNotificationsRead() {
    if (!notificationUnreadCount) {
      return;
    }
    const previousNotifications = notifications;
    const previousUnreadCount = notificationUnreadCount;
    setNotifications((current) => current.map((item) => (item.unread ? { ...item, unread: false } : item)));
    setNotificationUnreadCount(0);
    try {
      const snapshot = await markAccountNotificationsReadAll();
      applyNotificationSnapshot(snapshot);
    } catch (error) {
      setNotifications(previousNotifications);
      setNotificationUnreadCount(previousUnreadCount);
      pushToast?.({
        type: "error",
        title: "\u6279\u91CF\u5DF2\u8BFB\u5931\u8D25",
        message: error instanceof Error ? error.message : "\u64CD\u4F5C\u5931\u8D25",
      });
    }
  }

  async function handleDeleteNotifications(notificationIds) {
    const ids = Array.from(new Set((Array.isArray(notificationIds) ? notificationIds : []).map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)));
    if (!ids.length) {
      return;
    }
    const idSet = new Set(ids);
    const previousNotifications = notifications;
    const previousUnreadCount = notificationUnreadCount;
    const previousSelectedIds = new Set(selectedNotificationIds);
    const previousExpandedId = expandedNotificationId;
    const deletedUnreadCount = previousNotifications.reduce((count, item) => count + (idSet.has(item.id) && item.unread ? 1 : 0), 0);
    setNotifications((current) => current.filter((item) => !idSet.has(item.id)));
    setNotificationUnreadCount((current) => Math.max(0, current - deletedUnreadCount));
    setSelectedNotificationIds((current) => {
      const next = new Set(current);
      ids.forEach((notificationId) => next.delete(notificationId));
      return next;
    });
    setExpandedNotificationId((current) => (idSet.has(current) ? null : current));
    try {
      const snapshot = ids.length === 1
        ? await deleteAccountNotification(ids[0])
        : await bulkDeleteAccountNotifications(ids);
      applyNotificationSnapshot(snapshot);
    } catch (error) {
      setNotifications(previousNotifications);
      setNotificationUnreadCount(previousUnreadCount);
      setSelectedNotificationIds(previousSelectedIds);
      setExpandedNotificationId(previousExpandedId);
      pushToast?.({
        type: "error",
        title: "\u5220\u9664\u901A\u77E5\u5931\u8D25",
        message: error instanceof Error ? error.message : "\u64CD\u4F5C\u5931\u8D25",
      });
    }
  }

  function handleSettingsItemToggle(itemId) {
    if (settingsAction) {
      return;
    }
    setSettingsExpandedItem((current) => (current === itemId ? "" : itemId));
  }

  async function handleChangePassword(event) {
    event.preventDefault();
    if (settingsAction) {
      return;
    }
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const currentPassword = String(data.current_password || "");
    const newPassword = String(data.new_password || "");
    const confirmPassword = String(data.confirm_password || "");
    if (!currentPassword || !newPassword || !confirmPassword) {
      pushToast?.({
        type: "error",
        title: "修改失败",
        message: "请完整填写当前密码、新密码和确认密码。",
      });
      return;
    }
    if (newPassword.length < 6) {
      pushToast?.({
        type: "error",
        title: "修改失败",
        message: "新密码至少需要 6 位。",
      });
      return;
    }
    if (confirmPassword !== newPassword) {
      pushToast?.({
        type: "error",
        title: "修改失败",
        message: "两次输入的新密码不一致。",
      });
      return;
    }
    setSettingsAction("password");
    try {
      const result = await updateAccountPassword(currentPassword, newPassword);
      await onAuthenticated(authInfoWithUser(authInfo, result?.user));
      form.reset();
      setSettingsExpandedItem("");
      pushToast?.({
        type: "success",
        title: "密码已更新",
        message: "新密码已生效，其他会话已失效。",
      });
    } catch (error) {
      pushToast?.({
        type: "error",
        title: "修改密码失败",
        message: error instanceof Error ? error.message : "操作失败",
      });
    } finally {
      setSettingsAction("");
    }
  }

  async function handleChangeEmail(event) {
    event.preventDefault();
    if (settingsAction) {
      return;
    }
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const currentPassword = String(data.current_password || "");
    const normalizedEmail = normalizeOptionalText(data.email);
    if (!currentPassword || !normalizedEmail) {
      pushToast?.({
        type: "error",
        title: "绑定邮箱失败",
        message: "请填写当前密码和新的邮箱地址。",
      });
      return;
    }
    setSettingsAction("email");
    try {
      const result = await updateAccountEmail(currentPassword, normalizedEmail);
      await onAuthenticated(authInfoWithUser(authInfo, result?.user));
      form.reset();
      setSettingsExpandedItem("");
      pushToast?.({
        type: "success",
        title: "邮箱已绑定",
        message: normalizedEmail,
      });
    } catch (error) {
      pushToast?.({
        type: "error",
        title: "绑定邮箱失败",
        message: error instanceof Error ? error.message : "操作失败",
      });
    } finally {
      setSettingsAction("");
    }
  }

  async function handleChangePhone(event) {
    event.preventDefault();
    if (settingsAction) {
      return;
    }
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const currentPassword = String(data.current_password || "");
    const normalizedPhone = normalizeOptionalText(data.phone);
    if (!currentPassword || !normalizedPhone) {
      pushToast?.({
        type: "error",
        title: "绑定手机号失败",
        message: "请填写当前密码和新的手机号。",
      });
      return;
    }
    setSettingsAction("phone");
    try {
      const result = await updateAccountPhone(currentPassword, normalizedPhone);
      await onAuthenticated(authInfoWithUser(authInfo, result?.user));
      form.reset();
      setSettingsExpandedItem("");
      pushToast?.({
        type: "success",
        title: "手机号已绑定",
        message: normalizedPhone,
      });
    } catch (error) {
      pushToast?.({
        type: "error",
        title: "绑定手机号失败",
        message: error instanceof Error ? error.message : "操作失败",
      });
    } finally {
      setSettingsAction("");
    }
  }

  function handleNavSelect(item) {
    if (item.view) {
      setActiveView(item.view);
      return;
    }
    setActiveView(ACCOUNT_CENTER_DEFAULT_VIEW);
    if (item.target) {
      window.requestAnimationFrame(() => {
        document.getElementById(item.target)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  function handleQuickAction(item) {
    const action = item?.action;
    if (!action?.view) {
      return;
    }
    setActiveView(action.view);
    if (action.view === "settings") {
      setSettingsExpandedItem(action.section || "");
    }
  }

  function handleNotificationToggle(notificationId) {
    setExpandedNotificationId((current) => (current === notificationId ? null : notificationId));
    const targetNotification = notifications.find((item) => item.id === notificationId);
    if (targetNotification?.unread) {
      void markNotificationAsRead(notificationId);
    }
  }

  function handleNotificationSelection(notificationId, checked) {
    setSelectedNotificationIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(notificationId);
      } else {
        next.delete(notificationId);
      }
      return next;
    });
  }

  return (
    <div className="account-center">
      <aside className="account-center-sidebar" aria-label="用户中心导航">
        <div className="account-center-title">
          <span className="account-center-title-icon">
            <UserRound size={24} aria-hidden="true" />
          </span>
          <div>
            <h2 id="auth-dialog-title">用户中心</h2>
            <p>管理您的账户信息与服务</p>
          </div>
        </div>

        <nav className="account-center-nav" aria-label="账户服务">
          {ACCOUNT_CENTER_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item.view ? activeView === item.view : item.active && activeView === ACCOUNT_CENTER_DEFAULT_VIEW;
            return (
              <button
                className={isActive ? "account-center-nav-item is-active" : "account-center-nav-item"}
                type="button"
                aria-current={isActive ? "page" : undefined}
                onClick={() => handleNavSelect(item)}
                key={item.label}
              >
                <Icon size={20} aria-hidden="true" />
                <span>{item.label}</span>
                {isActive ? <ChevronRight size={18} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </nav>

        <button
          className="account-center-logout"
          type="button"
          disabled={logoutPending}
          onClick={onLogout}
        >
          {logoutPending ? <Loader2 className="spin" size={18} aria-hidden="true" /> : <LogOut size={18} aria-hidden="true" />}
          <span>{logoutPending ? "正在退出" : "退出登录"}</span>
        </button>
      </aside>

      <div className="account-center-content" ref={accountCenterContentRef}>
        {loginError ? <div className="auth-error account-center-error">{loginError}</div> : null}

        {activeView === "notifications" ? (
          <AccountCenterNotificationsView
            expandedNotificationId={expandedNotificationId}
            isLoading={notificationsLoading}
            notificationFilter={notificationFilter}
            notificationTypeFilter={notificationTypeFilter}
            notifications={notifications}
            onDeleteNotification={(notificationId) => handleDeleteNotifications([notificationId])}
            onDeleteSelected={() => handleDeleteNotifications(Array.from(selectedNotificationIds))}
            onFilterChange={setNotificationFilter}
            onMarkAllRead={handleMarkAllNotificationsRead}
            onToggleNotification={handleNotificationToggle}
            onToggleSelection={handleNotificationSelection}
            onTypeFilterChange={setNotificationTypeFilter}
            selectedNotificationIds={selectedNotificationIds}
            unreadCount={notificationUnreadCount}
            visibleNotifications={visibleNotifications}
          />
        ) : activeView === "points" ? (
          <AccountCenterPointsView
            authInfo={authInfo}
            ledgerBalance={ledgerBalance}
            ledgerItems={ledgerItems}
            ledgerLoading={ledgerLoading}
            ledgerTotal={ledgerTotal}
            onAuthenticated={onAuthenticated}
            onLedgerReload={reloadLedgerState}
            pushToast={pushToast}
            user={user}
          />
        ) : activeView === "settings" ? (
          <AccountCenterSettingsView
            actionPending={settingsAction}
            displayEmail={displayEmail}
            displayPhone={displayPhone}
            emailValue={currentEmail}
            expandedItem={settingsExpandedItem}
            onChangeEmail={handleChangeEmail}
            onChangePassword={handleChangePassword}
            onChangePhone={handleChangePhone}
            onToggleItem={handleSettingsItemToggle}
            phoneValue={currentPhone}
          />
        ) : (
          <>
        <section className="account-profile-panel" id="account-overview" aria-label="账户概览">
          <div className="account-profile-identity">
            <span className="account-profile-avatar" aria-hidden="true">
              <UserRound size={54} />
            </span>
            <div className="account-profile-copy">
              <div className="account-profile-name-row">
                <strong>{displayName}</strong>
                <span className="account-profile-role">
                  <BadgeCheck size={14} aria-hidden="true" />
                  {roleLabel}
                </span>
              </div>
              <dl className="account-profile-meta">
                <div>
                  <dt>邮箱：</dt>
                  <dd>
                    {displayEmail}
                    <span className="account-profile-copy-icon" aria-hidden="true">
                      <Copy size={13} />
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>注册时间：</dt>
                  <dd>{createdAt}</dd>
                </div>
                <div>
                  <dt>最后登录：</dt>
                  <dd>{lastLoginAt}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="account-balance-panel">
            <span>账户积分余额</span>
            <strong>{displayCredits} 积分</strong>
            <div className="account-balance-actions" aria-label="余额操作占位">
              {isAdmin ? (
                <a className="account-balance-admin-link" href="/admin">
                  <ShieldCheck size={17} aria-hidden="true" />
                  进入管理后台
                </a>
              ) : null}
              <span className="account-balance-link">
                <List size={17} aria-hidden="true" />
                资金明细
              </span>
            </div>
          </div>
        </section>

        <section className="account-stats-grid" id="account-points" aria-label="账户数据">
          {accountStats.map((item) => {
            const Icon = item.icon;
            return (
              <article className={`account-stat-card account-stat-card-${item.tone}`} key={item.label}>
                <div className="account-stat-label">
                  <span className="account-stat-icon">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <span>{item.label}</span>
                </div>
                <strong>
                  {item.value}
                  {item.unit ? <small>{item.unit}</small> : null}
                </strong>
                <span className="account-stat-action">
                  {item.action}
                  <ChevronRight size={18} aria-hidden="true" />
                </span>
              </article>
            );
          })}
        </section>

        <section className="account-quick-panel" id="account-quick-actions" aria-labelledby="account-quick-title">
          <h3 id="account-quick-title">快捷功能</h3>
          <div className="account-quick-grid">
            {ACCOUNT_CENTER_QUICK_ACTIONS.map((item) => {
              const Icon = item.icon;
              const itemContent = (
                <>
                  <span className="account-quick-icon">
                    <Icon size={21} aria-hidden="true" />
                  </span>
                  <span className="account-quick-text">
                    <strong>{item.title}</strong>
                    <small>{item.text}</small>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" />
                </>
              );
              if (item.action) {
                return (
                  <button
                    className="account-quick-item is-interactive"
                    type="button"
                    onClick={() => handleQuickAction(item)}
                    key={item.title}
                  >
                    {itemContent}
                  </button>
                );
              }
              return (
                <article className="account-quick-item" key={item.title}>
                  {itemContent}
                </article>
              );
            })}
          </div>
        </section>

        <section className="account-invite-banner" id="account-invite" aria-label="邀请推广">
          <div>
            <strong>邀请好友，赢取丰厚奖励</strong>
            <span>
              每邀请一位好友，您将获得最高 <b>100</b> 积分奖励
            </span>
          </div>
          <span className="account-invite-cta">立即邀请</span>
        </section>
          </>
        )}
      </div>
    </div>
  );
}

function AccountCenterNotificationsView({
  expandedNotificationId,
  isLoading,
  notificationFilter,
  notificationTypeFilter,
  notifications,
  onDeleteNotification,
  onDeleteSelected,
  onFilterChange,
  onMarkAllRead,
  onToggleNotification,
  onToggleSelection,
  onTypeFilterChange,
  selectedNotificationIds,
  unreadCount,
  visibleNotifications,
}) {
  const selectedCount = selectedNotificationIds.size;

  return (
    <section className="account-workbench account-notification-board" aria-labelledby="account-notification-title">
      <div className="account-workbench-head">
        <div>
          <h3 id="account-notification-title">消息通知</h3>
          <p>系统消息、活动通知、账户提醒等</p>
        </div>
        <div className="account-workbench-actions">
          <button type="button" className="account-workbench-action" onClick={onMarkAllRead} disabled={isLoading || unreadCount === 0}>
            <Check size={16} aria-hidden="true" />
            <span>全部标为已读</span>
          </button>
          <button type="button" className="account-workbench-action is-danger" onClick={onDeleteSelected} disabled={isLoading || selectedCount === 0}>
            <Trash2 size={16} aria-hidden="true" />
            <span>批量删除</span>
          </button>
        </div>
      </div>

      <div className="account-notification-toolbar">
        <div className="account-notification-tabs" role="tablist" aria-label="消息筛选">
          {ACCOUNT_NOTIFICATION_FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={notificationFilter === item.value ? "account-notification-tab is-active" : "account-notification-tab"}
              role="tab"
              aria-selected={notificationFilter === item.value}
              onClick={() => onFilterChange(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <label className="account-notification-type">
          <span className="sr-only">消息类型</span>
          <select value={notificationTypeFilter} onChange={(event) => onTypeFilterChange(event.target.value)}>
            {ACCOUNT_NOTIFICATION_TYPE_FILTERS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <ChevronDown size={16} aria-hidden="true" />
        </label>
      </div>

      <div className="account-notification-list" role="list" aria-label="消息列表">
        {isLoading ? (
          <div className="account-empty-state account-notification-empty">
            <span className="account-empty-icon" aria-hidden="true">
              <Loader2 className="spin" size={22} />
            </span>
            <div>
              <strong>\u6B63\u5728\u52A0\u8F7D\u6D88\u606F</strong>
              <p>\u6B63\u5728\u4ECE\u670D\u52A1\u5668\u540C\u6B65\u7AD9\u5185\u4FE1\u606F\u3002</p>
            </div>
          </div>
        ) : visibleNotifications.length ? (
          visibleNotifications.map((notification) => {
            const Icon = notification.icon;
            const isExpanded = expandedNotificationId === notification.id;
            const isSelected = selectedNotificationIds.has(notification.id);

            return (
              <article
                className={isExpanded ? "account-notification-item is-expanded" : "account-notification-item"}
                key={notification.id}
                role="listitem"
              >
                <label className="account-notification-check">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(event) => onToggleSelection(notification.id, event.target.checked)}
                    aria-label={`选择 ${notification.title}`}
                  />
                </label>

                <button
                  type="button"
                  className="account-notification-main"
                  aria-expanded={isExpanded}
                  onClick={() => onToggleNotification(notification.id)}
                >
                  <span className={`account-notification-icon account-notification-tone-${notification.tone}`}>
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <span className="account-notification-copy">
                    <span className="account-notification-headline">
                      <strong>{notification.title}</strong>
                      {notification.unread ? <span className="account-notification-dot" aria-hidden="true" /> : null}
                      <span className="account-notification-expand">{isExpanded ? "收起详情" : "展开详情"}</span>
                    </span>
                    <span className="account-notification-summary">{notification.summary}</span>
                  </span>
                </button>

                <div className="account-notification-meta">
                  <span className={`account-notification-type-pill account-notification-type-${notification.type}`}>
                    {notification.typeLabel}
                  </span>
                  <time dateTime={notification.time.replace(" ", "T")}>{notification.time}</time>
                  <button
                    type="button"
                    className="account-notification-delete"
                    aria-label={`删除 ${notification.title}`}
                    onClick={() => onDeleteNotification(notification.id)}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>

                {isExpanded ? (
                  <div className="account-notification-detail">
                    <p>{notification.detail}</p>
                  </div>
                ) : null}
              </article>
            );
          })
        ) : (
          <div className="account-empty-state account-notification-empty">
            <span className="account-empty-icon" aria-hidden="true">
              <Bell size={22} />
            </span>
            <div>
              <strong>没有符合条件的消息</strong>
              <p>试试切换筛选条件，或者清空类型筛选后再看。</p>
            </div>
          </div>
        )}
      </div>

      <div className="account-workbench-foot">
        <span>
          共 {notifications.length} 条消息，{unreadCount} 条未读
        </span>
        <span>{selectedCount ? `已选 ${selectedCount} 条` : "点击消息可展开详情"}</span>
      </div>
    </section>
  );
}

function AccountCenterPointsView({ authInfo, ledgerBalance, ledgerItems, ledgerLoading, ledgerTotal, onAuthenticated, onLedgerReload, pushToast, user }) {
  const currentCredits = ledgerLoading ? firstFiniteNumber(user?.credits, authInfo?.user?.credits, 0) : firstFiniteNumber(ledgerBalance, user?.credits, authInfo?.user?.credits, 0);
  const displayCredits = formatAccountNumber(currentCredits);
  const [selectedPackId, setSelectedPackId] = useState(ACCOUNT_POINTS_PACKAGES[0].id);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(ACCOUNT_POINTS_PAYMENT_METHODS[0].value);
  const [customAmount, setCustomAmount] = useState("100");
  const [voucherCode, setVoucherCode] = useState("");
  const [pointsFilter, setPointsFilter] = useState("all");
  const [pointsTypeFilter, setPointsTypeFilter] = useState("all");
  const [rechargePending, setRechargePending] = useState(false);
  const [rechargeError, setRechargeError] = useState("");
  const [rechargeSuccess, setRechargeSuccess] = useState("");

  const selectedPack = ACCOUNT_POINTS_PACKAGES.find((item) => item.id === selectedPackId) || ACCOUNT_POINTS_PACKAGES[0];
  const selectedPaymentMethodLabel = ACCOUNT_POINTS_PAYMENT_METHODS.find((item) => item.value === selectedPaymentMethod)?.label || "支付方式";
  const visibleRecords = useMemo(() => {
    return ledgerItems.filter((item) => {
      const isIncome = Number(item.change || 0) > 0;
      if (pointsFilter === "income" && !isIncome) {
        return false;
      }
      if (pointsFilter === "expense" && isIncome) {
        return false;
      }
      if (pointsTypeFilter !== "all" && item.type !== pointsTypeFilter) {
        return false;
      }
      return true;
    });
  }, [pointsFilter, pointsTypeFilter]);

  function resetRechargeState() {
    setRechargeError("");
    setRechargeSuccess("");
  }

  function handlePackSelect(packId) {
    setSelectedPackId(packId);
    resetRechargeState();
  }

  async function handleRechargeSubmit(event) {
    event.preventDefault();
    resetRechargeState();

    if (selectedPack.type === "voucher") {
      const code = voucherCode.trim();
      if (!code) {
        setRechargeError("请输入兑换码。");
        return;
      }
      setRechargePending(true);
      try {
        const result = await redeemCode(code);
        const resultAuth = authInfoWithUser(authInfo, result?.user);
        const nextAuth = await onAuthenticated(resultAuth);
        const addedCredits = Number(result?.credits || 0);
        const nextCredits = firstFiniteNumber(
          result?.user?.credits,
          result?.balance_after,
          result?.balance,
          nextAuth?.user?.credits,
          currentCredits + addedCredits,
          user?.credits,
          0,
        );
        const addedText = Number.isFinite(addedCredits) && addedCredits > 0
          ? `已兑换 ${addedCredits.toLocaleString("zh-CN")} 积分`
          : "兑换成功";
        const balanceText = Number.isFinite(nextCredits)
          ? `，当前余额 ${formatAccountNumber(nextCredits)} 积分`
          : "，积分余额已更新";
        setRechargeSuccess(`${addedText}${balanceText}`);
        setVoucherCode("");
        await onLedgerReload?.().catch(() => null);
        pushToast?.({
          type: "success",
          title: "兑换成功",
          message: Number.isFinite(nextCredits)
            ? `积分已到账，当前余额 ${formatAccountNumber(nextCredits)} 积分`
            : "积分已到账。",
        });
      } catch (error) {
        setRechargeError(error instanceof Error ? error.message : "兑换失败。");
      } finally {
        setRechargePending(false);
      }
      return;
    }

    if (selectedPack.type === "custom") {
      const amount = Number(customAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setRechargeError("请输入有效金额。");
        return;
      }
      pushToast?.({
        type: "info",
        title: "支付接口待接入",
        message: `已保留自定义金额 ${amount.toLocaleString("zh-CN")} 元，${selectedPaymentMethodLabel} 接入后再启用。`,
      });
      return;
    }

    pushToast?.({
      type: "info",
      title: "支付接口待接入",
      message: `${selectedPack.points.toLocaleString("zh-CN")} 积分充值入口已保留，${selectedPaymentMethodLabel} 接入后再启用。`,
    });
  }

  return (
    <section className="account-workbench account-points-board" aria-labelledby="account-points-title">
      <div className="account-workbench-head account-points-head">
        <div>
          <h3 id="account-points-title">我的积分</h3>
          <p>积分可用于兑换奖励，享受专属权益等</p>
        </div>
      </div>

      <section className="account-invite-banner account-points-banner" aria-label="积分概览">
        <div className="account-points-banner-main">
          <div className="account-points-banner-media" aria-hidden="true">
            <span className="account-points-banner-icon">
              <Coins size={30} />
            </span>
          </div>
          <div className="account-points-banner-copy">
            <span>当前积分余额</span>
            <strong>
              {displayCredits}
              <small>积分</small>
            </strong>
          </div>
        </div>

        <div className="account-points-banner-stats">
          {ACCOUNT_POINTS_BANNER_STATS.map((item, index) => {
            const Icon = item.icon;
            const tone = index === 0 ? "blue" : index === 1 ? "green" : "amber";
            return (
              <div className="account-points-banner-stat" key={item.title}>
                <span className={`account-points-banner-stat-icon account-notification-tone-${tone}`} aria-hidden="true">
                  <Icon size={16} />
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.text}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="account-points-recharge" aria-labelledby="account-points-recharge-title">
        <div className="account-workbench-head account-points-section-head">
          <div>
            <h4 id="account-points-recharge-title">充值积分</h4>
          </div>
        </div>

        <div className="account-points-pack-grid" role="list" aria-label="充值金额选择">
          {ACCOUNT_POINTS_PACKAGES.map((item) => {
            const Icon = item.icon;
            const isActive = selectedPackId === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={isActive ? "account-points-pack is-active" : "account-points-pack"}
                role="listitem"
                aria-pressed={isActive}
                onClick={() => handlePackSelect(item.id)}
              >
                <span className={`account-points-pack-icon account-notification-tone-${item.tone}`} aria-hidden="true">
                  <Icon size={16} />
                </span>
                <span className="account-points-pack-copy">
                  <strong>{item.label}</strong>
                  <span>{item.note}</span>
                </span>
                {isActive ? <Check className="account-points-pack-check" size={18} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>

        <form className="account-points-form" onSubmit={handleRechargeSubmit}>
          {selectedPack.type === "voucher" ? (
            <div className="account-points-voucher-row">
              <label className="account-points-voucher-field">
                <a href="https://pay.ldxp.cn/shop/ai12345" target="_blank" rel="noreferrer">
                  获取兑换码
                </a>
                <div className="account-points-voucher-input">
                  <Gift size={16} aria-hidden="true" />
                  <input
                    name="code"
                    type="text"
                    autoComplete="off"
                    placeholder="输入兑换码后提交"
                    value={voucherCode}
                    onChange={(event) => {
                      setVoucherCode(event.target.value);
                      resetRechargeState();
                    }}
                    required
                  />
                </div>
              </label>
              <button className="account-points-submit" type="submit" disabled={rechargePending}>
                {rechargePending ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Gift size={17} aria-hidden="true" />}
                <span>{rechargePending ? "处理中" : "使用兑换券充值"}</span>
              </button>
            </div>
          ) : (
            <>
              <div className="account-points-payment-row">
                <div className="account-points-payment-group" role="radiogroup" aria-label="支付方式">
                  {ACCOUNT_POINTS_PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon;
                    const isActive = selectedPaymentMethod === method.value;
                    return (
                      <label
                        key={method.value}
                        className={isActive ? "account-points-payment-method is-active" : "account-points-payment-method"}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.value}
                          checked={isActive}
                          onChange={() => setSelectedPaymentMethod(method.value)}
                        />
                        <span className={`account-points-payment-icon account-notification-tone-${method.tone}`} aria-hidden="true">
                          <Icon size={16} />
                        </span>
                        <span>{method.label}</span>
                        <span className="account-points-payment-radio" aria-hidden="true" />
                      </label>
                    );
                  })}
                </div>

                {selectedPack.type === "custom" ? (
                  <label className="account-points-custom-field">
                    <span>金额</span>
                    <input
                      name="customAmount"
                      type="number"
                      min="1"
                      step="1"
                      autoComplete="off"
                      placeholder="输入金额"
                      value={customAmount}
                      onChange={(event) => {
                        setCustomAmount(event.target.value);
                        resetRechargeState();
                      }}
                    />
                  </label>
                ) : null}
                <button className="account-points-submit" type="submit" disabled={rechargePending}>
                  {rechargePending ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <CircleDollarSign size={17} aria-hidden="true" />}
                  <span>{rechargePending ? "处理中" : "立即支付"}</span>
                </button>
              </div>
            </>
          )}

          {rechargeError ? <div className="auth-error">{rechargeError}</div> : null}
          {rechargeSuccess ? <div className="auth-success">{rechargeSuccess}</div> : null}
        </form>
      </section>

      <section className="account-points-detail" aria-labelledby="account-points-detail-title">
        <div className="account-workbench-head account-points-section-head">
          <div className="account-points-detail-heading">
            <h4 id="account-points-detail-title">积分明细</h4>
            <div className="account-points-tabs" role="tablist" aria-label="积分筛选">
              {ACCOUNT_POINTS_RECORD_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={pointsFilter === item.value ? "account-points-tab is-active" : "account-points-tab"}
                  role="tab"
                  aria-selected={pointsFilter === item.value}
                  onClick={() => setPointsFilter(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="account-points-toolbar">
            <label className="account-points-type">
              <span className="sr-only">积分类型</span>
              <select value={pointsTypeFilter} onChange={(event) => setPointsTypeFilter(event.target.value)}>
                {ACCOUNT_POINTS_RECORD_TYPE_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} aria-hidden="true" />
            </label>
          </div>
        </div>

        <div className="account-points-table" role="table" aria-label="积分明细列表">
          <div className="account-points-table-head" role="row">
            <span>时间</span>
            <span>类型</span>
            <span>描述</span>
            <span>积分变动</span>
            <span>余额</span>
          </div>

          <div className="account-points-table-body">
            {ledgerLoading ? (
              <div className="account-empty-state account-points-empty">
                <span className="account-empty-icon" aria-hidden="true">
                  <Loader2 className="spin" size={22} />
                </span>
                <div>
                  <strong>\u6B63\u5728\u52A0\u8F7D\u79EF\u5206\u660E\u7EC6</strong>
                  <p>\u6B63\u5728\u4ECE\u670D\u52A1\u5668\u540C\u6B65\u8D26\u6237\u79EF\u5206\u8BB0\u5F55\u3002</p>
                </div>
              </div>
            ) : visibleRecords.length ? (
              visibleRecords.map((record) => {
                const Icon = record.icon;
                const changeValue = Number(record.change || 0);
                const isIncome = changeValue > 0;
                const changeLabel = `${isIncome ? "+" : "-"}${formatAccountNumber(Math.abs(changeValue))}`;

                return (
                  <article className="account-points-row" key={record.id} role="row">
                    <div className="account-points-row-time" role="cell">
                      {record.time}
                    </div>
                    <div className="account-points-row-type" role="cell">
                      <span className={`account-points-row-icon account-notification-tone-${record.tone}`} aria-hidden="true">
                        <Icon size={14} />
                      </span>
                      <span>{record.typeLabel}</span>
                    </div>
                    <div className="account-points-row-desc" role="cell">
                      {record.description}
                    </div>
                    <div className={isIncome ? "account-points-row-change is-income" : "account-points-row-change is-expense"} role="cell">
                      {changeLabel}
                    </div>
                    <div className="account-points-row-balance" role="cell">
                      {formatAccountNumber(record.balance)}
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="account-empty-state account-points-empty">
                <span className="account-empty-icon" aria-hidden="true">
                  <CircleDollarSign size={22} />
                </span>
                <div>
                  <strong>没有符合条件的积分明细</strong>
                  <p>试试切换筛选条件，或者清空类型筛选后再看。</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="account-workbench-foot account-points-foot">
          <span>
            共 {ACCOUNT_POINTS_RECORD_TOTAL} 条记录，当前展示 {visibleRecords.length} 条
          </span>
        </div>
      </section>
    </section>
  );
}

function AccountCenterSettingsView({
  actionPending,
  displayEmail,
  displayPhone,
  emailValue,
  expandedItem,
  onChangeEmail,
  onChangePassword,
  onChangePhone,
  onToggleItem,
  phoneValue,
}) {
  const isPasswordExpanded = expandedItem === "password";
  const isEmailExpanded = expandedItem === "email";
  const isPhoneExpanded = expandedItem === "phone";
  const actionsLocked = Boolean(actionPending);

  return (
    <section className="account-workbench account-settings-board" id="account-settings" aria-labelledby="account-settings-title">
      <div className="account-workbench-head account-settings-head">
        <div>
          <h3 id="account-settings-title">账号设置</h3>
          <p>管理您的账户安全、登录方式和个人信息</p>
        </div>

        <div className="account-settings-security-card">
          <span className="account-settings-security-icon" aria-hidden="true">
            <ShieldCheck size={24} />
          </span>
          <div>
            <strong>
              账号安全等级：<span>高</span>
            </strong>
            <p>您的账号安全状况良好</p>
          </div>
        </div>
      </div>

      <section className="account-settings-group" aria-labelledby="account-settings-security-title">
        <h4 id="account-settings-security-title">安全设置</h4>
        <div className="account-settings-list">
          <article className={isPasswordExpanded ? "account-settings-item is-expanded" : "account-settings-item"} id="account-settings-password">
            <span className="account-settings-item-icon is-blue" aria-hidden="true">
              <LockKeyhole size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>修改密码</strong>
              <p>定期修改密码，保护账户安全</p>
            </div>
            <span className="account-settings-item-meta">上次修改：2026/05/05 20:07</span>
            <button
              type="button"
              className={isPasswordExpanded ? "account-settings-item-action is-open" : "account-settings-item-action"}
              onClick={() => onToggleItem("password")}
              disabled={actionsLocked}
              aria-expanded={isPasswordExpanded}
              aria-controls="account-settings-password-editor"
            >
              <span>{isPasswordExpanded ? "收起" : "修改"}</span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
            {isPasswordExpanded ? (
              <div className="account-settings-item-body" id="account-settings-password-editor">
                <form className="auth-form account-settings-inline-form" onSubmit={onChangePassword}>
                  <div className="account-settings-inline-grid account-settings-inline-grid-3">
                    <label>
                      <span>当前密码</span>
                      <input name="current_password" type="password" autoComplete="current-password" required />
                    </label>
                    <label>
                      <span>新密码</span>
                      <input name="new_password" type="password" minLength={6} autoComplete="new-password" required />
                    </label>
                    <label>
                      <span>确认新密码</span>
                      <input name="confirm_password" type="password" minLength={6} autoComplete="new-password" required />
                    </label>
                  </div>
                  <div className="account-settings-inline-actions">
                    <button type="button" className="auth-submit auth-secondary" onClick={() => onToggleItem("password")} disabled={actionPending === "password"}>
                      取消
                    </button>
                    <button className="auth-submit" type="submit" disabled={actionPending === "password"}>
                      {actionPending === "password" ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Check size={17} aria-hidden="true" />}
                      <span>{actionPending === "password" ? "保存中" : "保存密码"}</span>
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </article>

          <article className={isEmailExpanded ? "account-settings-item is-expanded" : "account-settings-item"} id="account-settings-email">
            <span className="account-settings-item-icon is-violet" aria-hidden="true">
              <Mail size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>绑定邮箱</strong>
              <p>用于接收验证码、通知等重要信息</p>
            </div>
            <span className="account-settings-item-meta">{displayEmail}</span>
            <button
              type="button"
              className={isEmailExpanded ? "account-settings-item-action is-open" : "account-settings-item-action"}
              onClick={() => onToggleItem("email")}
              disabled={actionsLocked}
              aria-expanded={isEmailExpanded}
              aria-controls="account-settings-email-editor"
            >
              <span>{isEmailExpanded ? "收起" : "修改"}</span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
            {isEmailExpanded ? (
              <div className="account-settings-item-body" id="account-settings-email-editor">
                <form className="auth-form account-settings-inline-form" onSubmit={onChangeEmail}>
                  <div className="account-settings-inline-grid account-settings-inline-grid-2">
                    <label>
                      <span>当前密码</span>
                      <input name="current_password" type="password" autoComplete="current-password" required />
                    </label>
                    <label>
                      <span>新邮箱</span>
                      <input name="email" type="email" autoComplete="email" defaultValue={emailValue || ""} placeholder="输入新的邮箱地址" required />
                    </label>
                  </div>
                  <div className="account-settings-inline-actions">
                    <button type="button" className="auth-submit auth-secondary" onClick={() => onToggleItem("email")} disabled={actionPending === "email"}>
                      取消
                    </button>
                    <button className="auth-submit" type="submit" disabled={actionPending === "email"}>
                      {actionPending === "email" ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Check size={17} aria-hidden="true" />}
                      <span>{actionPending === "email" ? "保存中" : "保存邮箱"}</span>
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </article>

          <article className={isPhoneExpanded ? "account-settings-item is-expanded" : "account-settings-item"} id="account-settings-phone">
            <span className="account-settings-item-icon is-green" aria-hidden="true">
              <Smartphone size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>绑定手机号</strong>
              <p>用于登录验证和安全验证</p>
            </div>
            <span className="account-settings-item-meta">{displayPhone}</span>
            <button
              type="button"
              className={isPhoneExpanded ? "account-settings-item-action is-open" : "account-settings-item-action"}
              onClick={() => onToggleItem("phone")}
              disabled={actionsLocked}
              aria-expanded={isPhoneExpanded}
              aria-controls="account-settings-phone-editor"
            >
              <span>{isPhoneExpanded ? "收起" : "修改"}</span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
            {isPhoneExpanded ? (
              <div className="account-settings-item-body" id="account-settings-phone-editor">
                <form className="auth-form account-settings-inline-form" onSubmit={onChangePhone}>
                  <div className="account-settings-inline-grid account-settings-inline-grid-2">
                    <label>
                      <span>当前密码</span>
                      <input name="current_password" type="password" autoComplete="current-password" required />
                    </label>
                    <label>
                      <span>新手机号</span>
                      <input name="phone" type="tel" inputMode="tel" autoComplete="tel" defaultValue={phoneValue || ""} placeholder="输入新的手机号" required />
                    </label>
                  </div>
                  <div className="account-settings-inline-actions">
                    <button type="button" className="auth-submit auth-secondary" onClick={() => onToggleItem("phone")} disabled={actionPending === "phone"}>
                      取消
                    </button>
                    <button className="auth-submit" type="submit" disabled={actionPending === "phone"}>
                      {actionPending === "phone" ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Check size={17} aria-hidden="true" />}
                      <span>{actionPending === "phone" ? "保存中" : "保存手机号"}</span>
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </article>

          <article className="account-settings-item">
            <span className="account-settings-item-icon is-orange" aria-hidden="true">
              <ShieldCheck size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>登录设备管理</strong>
              <p>管理您的登录设备，保障账户安全</p>
            </div>
            <span className="account-settings-item-meta">3 台设备在线</span>
            <button type="button" className="account-settings-item-action">
              查看
            </button>
          </article>
        </div>
      </section>

      <section className="account-settings-group" aria-labelledby="account-settings-account-title">
        <h4 id="account-settings-account-title">账户管理</h4>
        <div className="account-settings-list">
          <article className="account-settings-item">
            <span className="account-settings-item-icon is-blue" aria-hidden="true">
              <UserRound size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>个人信息</strong>
              <p>管理您的个人资料和展示信息</p>
            </div>
            <button type="button" className="account-settings-item-action">
              编辑
            </button>
          </article>

          <article className="account-settings-item">
            <span className="account-settings-item-icon is-violet" aria-hidden="true">
              <UsersRound size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>第三方账号绑定</strong>
              <p>绑定第三方账号，便捷登录</p>
            </div>
            <div className="account-settings-bindings" aria-label="第三方账号图标">
              <span className="account-settings-binding-dot">
                <MessageCircle size={14} aria-hidden="true" />
              </span>
              <span className="account-settings-binding-dot">
                <Mail size={14} aria-hidden="true" />
              </span>
              <span className="account-settings-binding-dot">
                <Monitor size={14} aria-hidden="true" />
              </span>
              <span className="account-settings-binding-dot">
                <ShieldCheck size={14} aria-hidden="true" />
              </span>
            </div>
            <button type="button" className="account-settings-item-action">
              管理
            </button>
          </article>

          <article className="account-settings-item is-danger">
            <span className="account-settings-item-icon is-danger" aria-hidden="true">
              <AlertTriangle size={18} />
            </span>
            <div className="account-settings-item-copy">
              <strong>注销账号</strong>
              <p>永久删除您的账号及所有数据，请谨慎操作</p>
            </div>
            <button type="button" className="account-settings-item-danger">
              注销账号
            </button>
          </article>
        </div>
      </section>

      <section className="account-settings-help" aria-label="帮助信息">
        <div>
          <span className="account-settings-help-icon" aria-hidden="true">
            <CircleHelp size={18} />
          </span>
          <div>
            <strong>遇到问题？</strong>
            <p>如果您在账号设置过程中遇到任何问题，可以查看 帮助中心 或 联系客服 获取支持</p>
          </div>
        </div>
        <button type="button" className="account-settings-help-action">
          <MessageCircle size={16} aria-hidden="true" />
          <span>联系客服</span>
        </button>
      </section>
    </section>
  );
}

function formatAccountNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("zh-CN") : "0";
}

function formatAccountRole(user) {
  const role = String(user?.role || "").trim();
  if (user?.is_admin || role === "admin") {
    return "管理员";
  }
  if (!role || role === "user") {
    return "普通用户";
  }
  return role;
}

function formatAccountDateTime(value) {
  return formatDateTime(value, "未记录");
}

function RedeemDialog({ authInfo, onClose, onAuthenticated }) {
  const [redeemError, setRedeemError] = useState("");
  const [redeemSuccess, setRedeemSuccess] = useState("");
  const [redeemPending, setRedeemPending] = useState(false);
  const closeButtonRef = useRef(null);
  const user = authInfo?.user || null;
  const currentCredits = Number(user?.credits || 0);
  const displayCredits = Number.isFinite(currentCredits) ? currentCredits.toLocaleString("zh-CN") : "0";

  useEffect(() => {
    closeButtonRef.current?.focus();
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function handleRedeemSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    setRedeemError("");
    setRedeemSuccess("");
    setRedeemPending(true);
    try {
      const result = await redeemCode(data.code);
      const resultAuth = authInfoWithUser(authInfo, result?.user);
      const nextAuth = await onAuthenticated(resultAuth);
      const addedCredits = Number(result?.credits || 0);
      const nextCredits = firstFiniteNumber(
        result?.user?.credits,
        result?.balance_after,
        result?.balance,
        nextAuth?.user?.credits,
        currentCredits + addedCredits,
        user?.credits,
        0,
      );
      const addedText = Number.isFinite(addedCredits) && addedCredits > 0
        ? `已兑换 ${addedCredits.toLocaleString("zh-CN")} 积分`
        : "兑换成功";
      const balanceText = Number.isFinite(nextCredits)
        ? `，当前余额 ${nextCredits.toLocaleString("zh-CN")} 积分。`
        : "，积分余额已更新。";
      setRedeemSuccess(`${addedText}${balanceText}`);
      form.reset();
    } catch (error) {
      setRedeemError(error instanceof Error ? error.message : "兑换失败");
    } finally {
      setRedeemPending(false);
    }
  }

  return (
    <div className="auth-overlay redeem-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="auth-dialog redeem-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="redeem-dialog-title"
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          className="auth-close"
          type="button"
          aria-label="关闭积分兑换窗口"
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>

        <div className="auth-heading">
          <div className="auth-mark">
            <Sparkles size={20} aria-hidden="true" />
          </div>
          <div>
            <h2 id="redeem-dialog-title">积分兑换</h2>
            <p>输入兑换码后立即写入当前账户积分。</p>
          </div>
        </div>

        <form className="auth-form redeem-form" onSubmit={handleRedeemSubmit}>
          <div className="credit-badge redeem-credit-badge">
            <span>{user?.username || "当前账户"}</span>
            <strong>{displayCredits} 积分</strong>
          </div>
          <label>
            <span>兑换码</span>
            <input name="code" type="text" autoComplete="off" placeholder="输入管理员发放的兑换码" required />
          </label>
          {redeemError ? <div className="auth-error">{redeemError}</div> : null}
          {redeemSuccess ? <div className="auth-success">{redeemSuccess}</div> : null}
          <button className="auth-submit" type="submit" disabled={redeemPending}>
            {redeemPending ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Sparkles size={17} aria-hidden="true" />}
            <span>{redeemPending ? "正在兑换" : "兑换积分"}</span>
          </button>
          <p className="redeem-note">兑换码只能使用一次；如提示无效、过期或已使用，请联系管理员重新生成。</p>
        </form>
      </section>
    </div>
  );
}

function AnnouncementDialog({ items, isPending, onClose, onMarkRead }) {
  const closeButtonRef = useRef(null);
  const visibleItems = Array.isArray(items) ? items : [];

  useEffect(() => {
    closeButtonRef.current?.focus();
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="auth-overlay redeem-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="auth-dialog redeem-dialog announcement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-dialog-title"
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          className="auth-close"
          type="button"
          aria-label="关闭公告弹窗"
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>

        <div className="auth-heading">
          <div className="auth-mark">
            <Megaphone size={20} aria-hidden="true" />
          </div>
          <div>
            <h2 id="announcement-dialog-title">公告通知</h2>
            <p>系统通知与公告通知会在这里集中显示。</p>
          </div>
        </div>

        <div className="auth-form announcement-dialog-content">
          <div className="credit-badge announcement-dialog-badge">
            <span>新通知</span>
            <strong>{visibleItems.length} 条</strong>
          </div>

          <div className="announcement-dialog-list" aria-label="新公告通知列表">
            {visibleItems.map((notification) => {
              const Icon = notification.icon || Bell;
              const detail = notification.detail || notification.summary || "暂无详细内容。";
              const summary = notification.summary && notification.summary !== detail ? notification.summary : "";
              return (
                <article className="announcement-dialog-item" key={notification.id}>
                  <div className="announcement-dialog-item-head">
                    <span className={`account-notification-icon account-notification-tone-${notification.tone}`} aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <div>
                      <strong>{notification.title}</strong>
                      {summary ? <span>{summary}</span> : null}
                    </div>
                    <span className={`account-notification-type-pill account-notification-type-${notification.type}`}>
                      {notification.typeLabel}
                    </span>
                  </div>
                  <p>{detail}</p>
                  <time dateTime={notification.time === "未记录" ? undefined : notification.time.replace(" ", "T")}>
                    {notification.time}
                  </time>
                </article>
              );
            })}
          </div>

          <div className="announcement-dialog-actions">
            <button type="button" className="auth-submit auth-secondary" onClick={onClose} disabled={isPending}>
              稍后再看
            </button>
            <button type="button" className="auth-submit" onClick={onMarkRead} disabled={isPending || visibleItems.length === 0}>
              {isPending ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Check size={17} aria-hidden="true" />}
              <span>{isPending ? "处理中" : "我已阅读"}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function getModelCreditCost(settings, modelId, resolution = STANDARD_RESOLUTION) {
  const option = getModelOption(settings, modelId);
  const sizeMapping = getModelSizeMapping(option, resolution);
  const cost = Number(sizeMapping?.credit_cost ?? option?.credit_cost ?? 1);
  return Number.isFinite(cost) && cost >= 0 ? Math.floor(cost) : 1;
}

function ModelField({ label, value, options, onChange }) {
  return (
    <ListboxField
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      placeholder="选择模型"
    />
  );
}

function AspectRatioField({ label, value, options, onChange }) {
  return <ListboxField label={label} value={value} options={options} onChange={onChange} />;
}

function EmptyState() {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <Sparkles size={30} />
      </div>
      <h2>准备就绪</h2>
      <p>
          描述你的想法或上传图片，开始使用 <span>Image Pro</span> 生成图像。
      </p>
    </div>
  );
}

const USER_MESSAGE_COLLAPSE_LENGTH = 520;
const USER_MESSAGE_COLLAPSE_LINES = 8;
const CHAT_SCROLL_BOTTOM_THRESHOLD = 24;
const MAX_CHAT_ATTACHMENTS = 6;
const CHAT_ATTACHMENT_ACCEPT = ".csv,.json,.md,.pdf,.txt,image/*";
const CHAT_IMAGE_ONLY_PROMPT = "请查看我上传的图片。";

function isImageSource(src) {
  return /^data:image\//i.test(src) || /\.(avif|gif|jpe?g|png|svg|webp)(\?|#|$)/i.test(src);
}

function isVideoSource(src) {
  return /^data:video\//i.test(src) || /\.(mp4|webm|mov)(\?|#|$)/i.test(src);
}

function isSafeMediaSource(src) {
  return /^(data:(?:image|video)\/|https?:\/\/|\/(?:api\/chat\/(?:uploads|artifacts)|generated|uploads)\/)/i.test(String(src || ""));
}

function isSafeLinkHref(href) {
  return /^(https?:\/\/|\/)/i.test(String(href || ""));
}

const CHAT_INLINE_URL_PATTERN = /https?:\/\/[^\s<>"'`]+/gi;

function normalizeInlineHref(value) {
  return String(value || "")
    .trim()
    .replace(/[)\]\}.,;:!?，。；：！？]+$/g, "");
}

function isLongUserMessage(content) {
  const text = String(content || "");
  return text.length > USER_MESSAGE_COLLAPSE_LENGTH || text.split(/\r?\n/).length > USER_MESSAGE_COLLAPSE_LINES;
}

function isChatImageAttachment(item) {
  return item?.kind === "image" || String(item?.mimeType || "").startsWith("image/");
}

function normalizeChatArtifacts(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => {
    const artifact = item && typeof item === "object" ? item : {};
    const mimeType = String(artifact.mimeType || artifact.mime_type || "");
    return {
      id: String(artifact.id || artifact.url || artifact.name || ""),
      name: String(artifact.name || "生成文件"),
      size: Number(artifact.size || 0),
      mimeType,
      kind: artifact.kind === "image" || mimeType.startsWith("image/") ? "image" : "file",
      url: String(artifact.url || ""),
      source: String(artifact.source || ""),
    };
  }).filter((item) => item.url && isSafeLinkHref(item.url));
}

function isChatArtifactImage(item) {
  return item?.kind === "image" || String(item?.mimeType || "").startsWith("image/");
}

function chatArtifactDownloadName(item) {
  const name = String(item?.name || "").trim();
  return name || "chat-artifact";
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) {
    return "";
  }
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function fileExtensionLabel(name) {
  const ext = String(name || "").split(".").pop() || "";
  return ext && ext !== name ? ext.slice(0, 4).toUpperCase() : "FILE";
}

function revokeAttachmentPreview(item) {
  if (item?.previewUrl && item.previewUrl.startsWith("blob:")) {
    URL.revokeObjectURL(item.previewUrl);
  }
}

function cleanupAttachmentPreviews(items) {
  (items || []).forEach(revokeAttachmentPreview);
}

function createAttachmentDraft(file, kindHint = "file") {
  const isImage = kindHint === "image" || String(file?.type || "").startsWith("image/");
  return {
    id: createClientId(),
    name: file?.name || "attachment",
    size: file?.size || 0,
    mimeType: file?.type || "",
    kind: isImage ? "image" : "file",
    status: "uploading",
    url: "",
    previewUrl: isImage && typeof URL !== "undefined" ? URL.createObjectURL(file) : "",
  };
}

function normalizeUploadedAttachment(draft, uploaded) {
  const next = {
    ...draft,
    ...uploaded,
    id: uploaded.id || draft.id,
    status: "ready",
    previewUrl: uploaded.kind === "image" ? (uploaded.url || draft.previewUrl) : "",
  };
  if (draft.previewUrl && draft.previewUrl.startsWith("blob:") && draft.previewUrl !== next.previewUrl) {
    URL.revokeObjectURL(draft.previewUrl);
  }
  return next;
}

function buildChatAttachmentText(attachments) {
  const ready = (attachments || []).filter((item) => item?.url);
  if (ready.length === 0) {
    return "";
  }
  return ready
    .map((item, index) => {
      const name = item.name || "附件";
      const meta = [item.mimeType, formatFileSize(item.size)].filter(Boolean).join("，");
      const textContent = String(item.textContent || "").trim();
      if (textContent) {
        const truncatedNote = item.textTruncated ? "\n[系统提示：附件内容过长，以上内容已截断。]" : "";
        return `${index + 1}. ${name}${meta ? `（${meta}）` : ""}\n${textContent}${truncatedNote}`;
      }
      return `${index + 1}. ${name}${meta ? `（${meta}）` : ""}\n[系统提示：该附件未自动解析为文本；请提示用户提供可读取的文本内容。]`;
    })
    .join("\n\n");
}

function buildChatRequestContent(text, attachments) {
  const trimmedText = String(text || "").trim();
  const ready = (attachments || []).filter((item) => item?.url);
  if (ready.length === 0) {
    return trimmedText;
  }
  const imageParts = ready
    .filter(isChatImageAttachment)
    .map((item) => ({ type: "image_url", image_url: { url: item.url } }));
  const fileText = buildChatAttachmentText(ready.filter((item) => !isChatImageAttachment(item)));
  const textParts = [
    trimmedText,
    fileText ? `附件：\n${fileText}` : "",
    !trimmedText && !fileText && imageParts.length > 0 ? CHAT_IMAGE_ONLY_PROMPT : "",
  ].filter(Boolean).join("\n\n");
  return [
    ...(textParts ? [{ type: "text", text: textParts }] : []),
    ...imageParts,
  ];
}

function chatContentToText(content) {
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    return content.map((item) => chatContentToText(item)).filter(Boolean).join("\n");
  }
  if (content && typeof content === "object") {
    if (typeof content.text === "string") {
      return content.text;
    }
    if (typeof content.content === "string") {
      return content.content;
    }
    const imageUrl = content.image_url?.url || content.image_url || "";
    return imageUrl ? `[图片] ${imageUrl}` : "";
  }
  return "";
}

function messageTextForCopy(message) {
  return chatContentToText(message?.content);
}

function chatReasoningToText(message) {
  return String(message?.reasoning || message?.reasoningContent || message?.reasoning_content || "");
}

function chatRetryNoticeFromEvent(event) {
  const upstreamStatus = Number(event?.upstreamStatus || event?.upstream_status || 0);
  const status = upstreamStatus || Number(event?.status || 0);
  const provider = String(event?.providerLabel || event?.providerId || "").trim();
  const attempt = Number(event?.attempt || 0);
  const maxAttempts = Number(event?.maxAttempts || 0);
  const statusText = status ? `HTTP ${status}` : "上游错误";
  const attemptText = attempt && maxAttempts ? `第 ${attempt}/${maxAttempts} 次尝试` : "当前通道";
  return `${attemptText}${provider ? `（${provider}）` : ""}返回 ${statusText}，正在切换通道重试`;
}

function formatChatElapsed(startedAt) {
  const timestamp = Number(startedAt || Date.now());
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  if (minutes <= 0) {
    return `${seconds}s`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function useChatElapsed(startedAt, active = true) {
  const [elapsed, setElapsed] = useState(() => formatChatElapsed(startedAt));

  useEffect(() => {
    setElapsed(formatChatElapsed(startedAt));
    if (!active) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setElapsed(formatChatElapsed(startedAt));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [active, startedAt]);

  return elapsed;
}

function matchLooseCodeFence(line) {
  const match = String(line || "").match(/^[ \t]{0,3}(`{1,2})[ \t]*([A-Za-z][\w.+-]{0,30})[ \t]*(?:\1)?[ \t]*$/);
  if (!match) {
    return null;
  }
  return { ticks: match[1], language: match[2] || "" };
}

function isLooseCodeFenceClose(line, ticks) {
  return String(line || "").trim() === ticks;
}

function stripLooseCodeFenceClose(line, ticks) {
  const text = String(line || "");
  const trimmedEnd = text.trimEnd();
  if (!ticks || !trimmedEnd.endsWith(ticks) || trimmedEnd.trim() === ticks) {
    return { line: text, closed: false };
  }
  return { line: trimmedEnd.slice(0, -ticks.length).trimEnd(), closed: true };
}

function hasLooseCodeFenceCloseAhead(lines, startIndex, ticks) {
  for (let offset = startIndex; offset < Math.min(lines.length, startIndex + 12); offset += 1) {
    if (isLooseCodeFenceClose(lines[offset], ticks)) {
      return true;
    }
  }
  return false;
}

function looksLikeCodeLine(line, language = "") {
  const text = String(line || "");
  const trimmed = text.trim();
  const normalizedLanguage = String(language || "").trim().toLowerCase();
  if (!trimmed) {
    return false;
  }
  if (/^[`~]{1,3}$/.test(trimmed)) {
    return false;
  }
  if (/^[ \t]{2,}\S/.test(text)) {
    return true;
  }
  if (["bash", "sh", "shell", "zsh", "powershell", "ps1"].includes(normalizedLanguage)) {
    return /^(?:[$#]\s*)?(?:cd|curl|docker|export|git|npm|openssl|pip|pnpm|python|set|source|ssh|unset|uv|yarn)\b/i.test(trimmed)
      || /^[A-Za-z_][\w-]*=/.test(trimmed);
  }
  if (["py", "python"].includes(normalizedLanguage)) {
    return /^(?:from|import|def|class|if|elif|else|for|while|with|try|except|finally|return|raise|print)\b/.test(trimmed)
      || /^[A-Za-z_]\w*\s*=/.test(trimmed)
      || /\b(?:requests|json|os|sys|Path)\./.test(trimmed);
  }
  return /(?:=>|[={};()[\]]|<\/?[A-Za-z][\w:-]*>|^\s*[-\w]+\s*:)/.test(trimmed);
}

function matchLooseCodeBlockStart(line, nextLine = "") {
  const looseFence = matchLooseCodeFence(line);
  return looseFence && looksLikeCodeLine(nextLine, looseFence.language) ? looseFence : null;
}

function isCodeContinuationLine(line) {
  const trimmed = String(line || "").trim();
  return Boolean(trimmed)
    && (/^[,.)}\]]/.test(trimmed)
      || /^[A-Za-z_$][\w$]*\s*:/.test(trimmed)
      || /^['"][^'"]+['"]\s*:/.test(trimmed)
      || /^(?:#|\/\/|\/\*)|\* /.test(trimmed));
}

function parseMarkdownBlocks(content) {
  const lines = String(content || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let index = 0;

  function matchCodeFence(line) {
    const match = String(line || "").match(/^([ \t]*)(`{3,}|~{3,})[ \t]*([\w.+-]*)[ \t]*$/);
    return match ? { indent: match[1] || "", marker: match[2] || "```", language: match[3] || "" } : null;
  }

  function isCodeFenceClose(line, fence) {
    const marker = String(fence?.marker || "```");
    const markerChar = marker[0] || "`";
    const markerLength = Math.max(marker.length, 3);
    const text = String(line || "").trim();
    return text.length >= markerLength && [...text].every((char) => char === markerChar);
  }

  function stripCodeFenceIndent(line, fence) {
    const indent = String(fence?.indent || "");
    const text = String(line || "");
    return indent && text.startsWith(indent) ? text.slice(indent.length) : text;
  }

  function isBlockStart(line, nextLine = "") {
    return Boolean(matchCodeFence(line))
      || Boolean(matchLooseCodeBlockStart(line, nextLine))
      || /^#{1,4}\s+/.test(line)
      || /^>\s?/.test(line)
      || /^(\s*[-*+]\s+|\s*\d+\.\s+)/.test(line)
      || (line.includes("|") && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(nextLine));
  }

  while (index < lines.length) {
    const line = lines[index] || "";
    if (!line.trim()) {
      index += 1;
      continue;
    }
    const trimmedLine = line.trim();
    if (isSafeMediaSource(trimmedLine) && (isImageSource(trimmedLine) || isVideoSource(trimmedLine))) {
      blocks.push({ type: "media", src: trimmedLine, alt: "media" });
      index += 1;
      continue;
    }
    const looseFence = matchLooseCodeBlockStart(line, lines[index + 1] || "");
    if (looseFence) {
      const codeLines = [];
      index += 1;
      while (index < lines.length) {
        const currentLine = lines[index] || "";
        if (isLooseCodeFenceClose(currentLine, looseFence.ticks)) {
          index += 1;
          break;
        }
        if (!currentLine.trim() && !hasLooseCodeFenceCloseAhead(lines, index + 1, looseFence.ticks)) {
          break;
        }
        if (
          codeLines.length > 0
          && !looksLikeCodeLine(currentLine, looseFence.language)
          && !isCodeContinuationLine(currentLine)
        ) {
          break;
        }
        const strippedLine = stripLooseCodeFenceClose(currentLine, looseFence.ticks);
        codeLines.push(strippedLine.line);
        index += 1;
        if (strippedLine.closed) {
          break;
        }
      }
      blocks.push({ type: "code", language: looseFence.language, value: codeLines.join("\n") });
      continue;
    }
    const fence = matchCodeFence(line);
    if (fence) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !isCodeFenceClose(lines[index], fence)) {
        codeLines.push(stripCodeFenceIndent(lines[index], fence));
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      blocks.push({ type: "code", language: fence.language, value: codeLines.join("\n") });
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, value: heading[2] });
      index += 1;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push({ type: "quote", value: quoteLines.join("\n") });
      continue;
    }
    if (/^(\s*[-*+]\s+|\s*\d+\.\s+)/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items = [];
      const itemPattern = ordered ? /^\s*\d+\.\s+/ : /^\s*[-*+]\s+/;
      while (index < lines.length && itemPattern.test(lines[index])) {
        items.push(lines[index].replace(itemPattern, ""));
        index += 1;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }
    if (line.includes("|") && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(lines[index + 1] || "")) {
      const rows = [line];
      index += 2;
      while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
        rows.push(lines[index]);
        index += 1;
      }
      blocks.push({ type: "table", rows: rows.map(splitMarkdownTableRow) });
      continue;
    }
    const paragraph = [line];
    index += 1;
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines[index], lines[index + 1] || "")) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push({ type: "paragraph", value: paragraph.join("\n") });
  }

  return blocks;
}

function splitMarkdownTableRow(row) {
  return String(row || "")
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function ChatMedia({ src, alt, onImageOpen, forceImage = false }) {
  if (!isSafeMediaSource(src)) {
    return alt || src;
  }
  if (isVideoSource(src)) {
    return <video className="chat-media-video" src={src} controls playsInline preload="metadata" />;
  }
  if (!forceImage && !isImageSource(src) && !/^data:image\//i.test(src)) {
    return (
      <a className="chat-inline-link" href={src} target="_blank" rel="noopener noreferrer">
        {alt || src}
      </a>
    );
  }
  const downloadItem = { model: "chat-image", imageUrl: src };
  return (
    <span className="chat-media-frame">
      <button
        className="chat-media-preview"
        type="button"
        onClick={() => onImageOpen?.({ src, alt: alt || "AI 回复图片" })}
        aria-label="查看大图"
      >
        <img src={src} alt={alt || "AI 回复图片"} loading="lazy" decoding="async" />
      </button>
      <a
        className="chat-media-download"
        href={src}
        download={buildDownloadName(downloadItem, src)}
        onClick={(event) => handleImageDownload(event, downloadItem, src)}
        aria-label="下载图片"
        title="下载图片"
      >
        <Download size={14} aria-hidden="true" />
      </a>
    </span>
  );
}

function renderInlineMarkdown(value, keyPrefix, options = {}) {
  const text = String(value || "");
  const pattern = /!\[(?<imageAlt>[^\]]*)\]\((?<imageSrc>[^)\s]+)\)|\[\[(?<citationLabel>[^\]]+)\]\]\((?<citationHref>[^)\s]+)\)|\[(?<linkLabel>[^\]]+)\]\((?<linkHref>[^)\s]+)\)|(?<codeTicks>`+)(?<codeText>[^`\n]+)\k<codeTicks>|\*\*(?<strongText>[^*]+)\*\*|\*(?<emText>[^*]+)\*/g;
  const nodes = [];
  let cursor = 0;
  let tokenIndex = 0;
  const seenCitations = new Set();

  function appendText(segment) {
    if (!segment) {
      return;
    }
    segment.split("\n").forEach((part, partIndex, parts) => {
      if (part) {
        let lastIndex = 0;
        let urlIndex = 0;
        for (const urlMatch of part.matchAll(CHAT_INLINE_URL_PATTERN)) {
          const rawUrl = urlMatch[0];
          const href = normalizeInlineHref(rawUrl);
          const matchIndex = urlMatch.index || 0;
          if (matchIndex > lastIndex) {
            nodes.push(part.slice(lastIndex, matchIndex));
          }
          if (href && isSafeLinkHref(href)) {
            nodes.push(
              <a
                className="chat-inline-link"
                key={`${keyPrefix}-url-${tokenIndex}-${partIndex}-${urlIndex}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {href}
              </a>,
            );
            if (rawUrl.length > href.length) {
              nodes.push(rawUrl.slice(href.length));
            }
          } else {
            nodes.push(rawUrl);
          }
          lastIndex = matchIndex + rawUrl.length;
          urlIndex += 1;
        }
        if (lastIndex < part.length) {
          nodes.push(part.slice(lastIndex));
        }
      }
      if (partIndex < parts.length - 1) {
        nodes.push(<br key={`${keyPrefix}-br-${tokenIndex}-${partIndex}`} />);
      }
    });
  }

  for (const match of text.matchAll(pattern)) {
    appendText(text.slice(cursor, match.index));
    const key = `${keyPrefix}-${tokenIndex}`;
    const groups = match.groups || {};
    if (groups.imageSrc != null) {
      const src = groups.imageSrc || "";
      if (isSafeMediaSource(src)) {
        nodes.push(<ChatMedia key={key} src={src} alt={groups.imageAlt || "media"} onImageOpen={options.onImageOpen} forceImage />);
      } else {
        nodes.push(match[0]);
      }
    } else if (groups.citationLabel != null) {
      const citationLabel = String(groups.citationLabel || "").trim();
      const href = normalizeInlineHref(groups.citationHref || "");
      const citationKey = `${citationLabel}:${href}`;
      if (seenCitations.has(citationKey)) {
        cursor = (match.index || 0) + match[0].length;
        tokenIndex += 1;
        continue;
      }
      seenCitations.add(citationKey);
      nodes.push(
        isSafeLinkHref(href)
          ? (
            <a
              className="chat-citation-link"
              key={key}
              href={href}
              target={/^https?:\/\//i.test(href) ? "_blank" : undefined}
              rel="noopener noreferrer"
              aria-label={`打开来源 ${citationLabel}`}
            >
              <span>{citationLabel}</span>
              <ExternalLink size={10} aria-hidden="true" />
            </a>
          )
          : match[0],
      );
    } else if (groups.linkLabel != null) {
      const href = normalizeInlineHref(groups.linkHref || "");
      nodes.push(
        isSafeLinkHref(href)
          ? (
            <a
              className="chat-inline-link"
              key={key}
              href={href}
              target={/^https?:\/\//i.test(href) ? "_blank" : undefined}
              rel="noopener noreferrer"
            >
              {groups.linkLabel}
            </a>
          )
          : match[0],
      );
    } else if (groups.codeText != null) {
      nodes.push(<code key={key}>{groups.codeText}</code>);
    } else if (groups.strongText != null) {
      nodes.push(<strong key={key}>{groups.strongText}</strong>);
    } else if (groups.emText != null) {
      nodes.push(<em key={key}>{groups.emText}</em>);
    }
    cursor = (match.index || 0) + match[0].length;
    tokenIndex += 1;
  }
  appendText(text.slice(cursor));
  return nodes;
}

const CODE_LANGUAGE_ALIASES = {
  bash: "shell",
  cjs: "javascript",
  html: "markup",
  js: "javascript",
  jsx: "javascript",
  md: "markdown",
  py: "python",
  sh: "shell",
  ts: "typescript",
  tsx: "typescript",
  xml: "markup",
  yml: "yaml",
};

const CODE_KEYWORDS = {
  css: ["@media", "@supports", "@keyframes", "from", "to", "important"],
  javascript: [
    "async", "await", "break", "case", "catch", "class", "const", "continue", "default", "delete", "do", "else",
    "export", "extends", "finally", "for", "from", "function", "if", "import", "in", "instanceof", "let", "new",
    "of", "return", "static", "switch", "this", "throw", "try", "typeof", "var", "void", "while", "yield",
  ],
  json: ["false", "null", "true"],
  markup: ["DOCTYPE"],
  markdown: ["TODO", "NOTE", "WARN"],
  python: [
    "and", "as", "assert", "async", "await", "break", "class", "continue", "def", "del", "elif", "else", "except",
    "finally", "for", "from", "global", "if", "import", "in", "is", "lambda", "nonlocal", "not", "or", "pass",
    "raise", "return", "try", "while", "with", "yield",
  ],
  shell: ["case", "do", "done", "elif", "else", "esac", "fi", "for", "function", "if", "in", "then", "while"],
  typescript: [
    "abstract", "as", "async", "await", "break", "case", "catch", "class", "const", "continue", "default", "delete",
    "do", "else", "enum", "export", "extends", "finally", "for", "from", "function", "if", "implements", "import",
    "in", "instanceof", "interface", "let", "namespace", "new", "of", "private", "protected", "public", "readonly",
    "return", "static", "switch", "this", "throw", "try", "type", "typeof", "var", "void", "while", "yield",
  ],
  yaml: ["false", "null", "true"],
};

function normalizeCodeLanguage(language) {
  const raw = String(language || "").trim().toLowerCase();
  return CODE_LANGUAGE_ALIASES[raw] || raw || "text";
}

function codeTokenClass(type) {
  return `chat-code-token ${type}`;
}

function keywordPatternFor(language) {
  const keywords = CODE_KEYWORDS[language] || CODE_KEYWORDS.javascript;
  return new RegExp(`^(?:${keywords.map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`);
}

function nextHighlightedToken(line, language) {
  const normalized = normalizeCodeLanguage(language);
  const commentPattern = normalized === "markup"
    ? /^(?:<!--.*?-->)/
    : ["python", "shell", "yaml"].includes(normalized)
      ? /^(?:#.*)/
      : /^(?:\/\/.*|\/\*.*?\*\/)/;
  const patterns = [
    ["comment", commentPattern],
    ["string", /^(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`)/],
    ["color", /^#[\da-f]{3,8}\b/i],
    ["number", /^-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b/i],
    ["keyword", keywordPatternFor(normalized)],
    ["boolean", /^(?:false|null|true|None|True|False)\b/],
    ["tag", /^(?:<\/?[A-Za-z][\w:-]*)/],
    ["attr", normalized === "markup" ? /^[A-Za-z_:][\w:.-]*(?=\=)/ : /^-?[A-Za-z_][\w-]*(?=\s*:)/],
    ["function", /^[A-Za-z_$][\w$]*(?=\s*\()/],
    ["operator", /^(?:=>|===|!==|==|!=|<=|>=|\+\+|--|\|\||&&|[+\-*/%=<>!?:|&~^])/],
  ];

  for (const [type, pattern] of patterns) {
    const match = line.match(pattern);
    if (match?.[0]) {
      return { type, value: match[0] };
    }
  }
  return { type: "", value: line[0] || "" };
}

function renderHighlightedCode(code, language) {
  const lines = String(code || "").split("\n");
  return lines.flatMap((line, lineIndex) => {
    const nodes = [];
    let remaining = line;
    let tokenIndex = 0;
    while (remaining) {
      const token = nextHighlightedToken(remaining, language);
      if (!token.value) {
        break;
      }
      nodes.push(
        token.type
          ? <span className={codeTokenClass(token.type)} key={`${lineIndex}-${tokenIndex}`}>{token.value}</span>
          : token.value,
      );
      remaining = remaining.slice(token.value.length);
      tokenIndex += 1;
    }
    if (lineIndex < lines.length - 1) {
      nodes.push("\n");
    }
    return nodes;
  });
}

function MarkdownContent({ content, onImageOpen, highlightCode = false }) {
  const blocks = parseMarkdownBlocks(content);
  if (blocks.length === 0) {
    return null;
  }
  return (
    <div className="chat-content chat-markdown">
      {blocks.map((block, index) => {
        const key = `md-${index}`;
        if (block.type === "code") {
          return <ChatCodeBlock code={block.value} highlight={highlightCode} key={key} language={block.language} />;
        }
        if (block.type === "media") {
          return <ChatMedia key={key} src={block.src} alt={block.alt} onImageOpen={onImageOpen} />;
        }
        if (block.type === "heading") {
          const Heading = `h${Math.min(block.level + 2, 5)}`;
          return <Heading key={key}>{renderInlineMarkdown(block.value, key, { onImageOpen })}</Heading>;
        }
        if (block.type === "quote") {
          return <blockquote key={key}>{renderInlineMarkdown(block.value, key, { onImageOpen })}</blockquote>;
        }
        if (block.type === "list") {
          const ListTag = block.ordered ? "ol" : "ul";
          return (
            <ListTag key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{renderInlineMarkdown(item, `${key}-${itemIndex}`, { onImageOpen })}</li>
              ))}
            </ListTag>
          );
        }
        if (block.type === "table") {
          const [head = [], ...rows] = block.rows;
          return (
            <div className="chat-table-scroll" key={key}>
              <table>
                <thead>
                  <tr>{head.map((cell, cellIndex) => <th key={`${key}-h-${cellIndex}`}>{renderInlineMarkdown(cell, `${key}-h-${cellIndex}`, { onImageOpen })}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={`${key}-r-${rowIndex}`}>
                      {row.map((cell, cellIndex) => <td key={`${key}-c-${rowIndex}-${cellIndex}`}>{renderInlineMarkdown(cell, `${key}-c-${rowIndex}-${cellIndex}`, { onImageOpen })}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={key}>{renderInlineMarkdown(block.value, key, { onImageOpen })}</p>;
      })}
    </div>
  );
}

function ChatCodeBlock({ code, language, highlight = false }) {
  const [copied, setCopied] = useState(false);
  const normalizedLanguage = normalizeCodeLanguage(language);

  async function copyCode() {
    await copyTextToClipboard(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return (
    <figure className="chat-code-block">
      <figcaption>
        <span>{language || "code"}</span>
        <button type="button" onClick={copyCode} aria-label="复制代码">
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          <span>{copied ? "已复制" : "复制"}</span>
        </button>
      </figcaption>
      <pre><code className={highlight ? `language-${normalizedLanguage}` : undefined}>{highlight ? renderHighlightedCode(code, normalizedLanguage) : code}</code></pre>
    </figure>
  );
}

function ChatAttachmentStrip({ attachments, className = "", onImageOpen, onRemove }) {
  const items = (attachments || []).filter(Boolean);
  if (items.length === 0) {
    return null;
  }
  return (
    <div className={className ? `chat-attachment-strip ${className}` : "chat-attachment-strip"}>
      {items.map((item) => {
        const isImage = isChatImageAttachment(item);
        const previewSrc = item.previewUrl || item.url;
        const title = [item.error || item.name || "附件", formatFileSize(item.size)].filter(Boolean).join(" · ");
        const tileClass = item.status === "error"
          ? "chat-attachment-tile is-error"
          : item.status === "uploading"
            ? "chat-attachment-tile is-uploading"
            : "chat-attachment-tile";
        return (
          <div className={tileClass} key={item.id || item.url || item.name} title={title}>
            {isImage && previewSrc ? (
              <button
                className="chat-attachment-thumb"
                type="button"
                onClick={() => onImageOpen?.({ src: item.url || previewSrc, alt: item.name || "上传图片" })}
                aria-label={`查看 ${item.name || "上传图片"}`}
              >
                <img src={previewSrc} alt={item.name || "上传图片"} />
              </button>
            ) : item.url ? (
              <a
                className="chat-attachment-file"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.name || "附件"}
              >
                <Paperclip size={16} aria-hidden="true" />
                <span>{fileExtensionLabel(item.name)}</span>
              </a>
            ) : (
              <span className="chat-attachment-file" aria-hidden="true">
                <Paperclip size={16} />
                <span>{fileExtensionLabel(item.name)}</span>
              </span>
            )}
            <span className="chat-attachment-name">{item.name || "附件"}</span>
            {item.status === "uploading" ? <span className="chat-attachment-state">上传中</span> : null}
            {item.status === "error" ? <span className="chat-attachment-state">失败</span> : null}
            {onRemove ? (
              <button
                className="chat-attachment-remove"
                type="button"
                onClick={() => onRemove(item.id)}
                aria-label={`移除 ${item.name || "附件"}`}
              >
                <X size={13} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ChatArtifactStrip({ artifacts, onImageOpen }) {
  const items = normalizeChatArtifacts(artifacts);
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="chat-artifact-strip">
      {items.map((item) => {
        const isImage = isChatArtifactImage(item) && isSafeMediaSource(item.url);
        const meta = [item.mimeType, formatFileSize(item.size)].filter(Boolean).join(" · ");
        const title = [item.name || "生成文件", meta].filter(Boolean).join(" · ");
        const key = item.id || item.url || item.name;
        return (
          <div className="chat-artifact-tile" key={key} title={title}>
            {isImage ? (
              <button
                className="chat-artifact-preview"
                type="button"
                onClick={() => onImageOpen?.({ src: item.url, alt: item.name || "生成图片" })}
                aria-label={`查看 ${item.name || "生成图片"}`}
              >
                <img src={item.url} alt={item.name || "生成图片"} loading="lazy" decoding="async" />
              </button>
            ) : (
              <a
                className="chat-artifact-file"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.name || "生成文件"}
              >
                <Paperclip size={16} aria-hidden="true" />
                <span>{fileExtensionLabel(item.name)}</span>
              </a>
            )}
            <span className="chat-artifact-copy">
              <span className="chat-artifact-name">{item.name || "生成文件"}</span>
              <span className="chat-artifact-meta">{meta || "生成文件"}</span>
            </span>
            <a
              className="chat-artifact-download"
              href={item.url}
              download={chatArtifactDownloadName(item)}
              aria-label={`下载 ${item.name || "生成文件"}`}
              title={`下载 ${item.name || "生成文件"}`}
            >
              <Download size={14} aria-hidden="true" />
            </a>
          </div>
        );
      })}
    </div>
  );
}

function ChatMessageActions({ message, copied, onCopy, onDelete }) {
  const canCopy = Boolean(messageTextForCopy(message).trim());
  return (
    <div className="chat-message-actions" aria-label="消息操作">
      <button type="button" onClick={() => onCopy(message)} disabled={!canCopy} aria-label="复制消息">
        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      </button>
      <button type="button" onClick={() => onDelete(message)} aria-label="删除消息">
        <Trash2 size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

function ChatReasoningBlock({ message }) {
  const [isOpen, setIsOpen] = useState(true);
  const collapsedForContentRef = useRef(false);
  const reasoning = chatReasoningToText(message).trim();
  const content = String(message.content || "").trim();
  const isPending = Boolean(message.pending);
  const shouldShow = Boolean(reasoning) || (isPending && !content);
  const elapsed = useChatElapsed(message.startedAt || message.createdAt, isPending && !content);

  useLayoutEffect(() => {
    if (content && !collapsedForContentRef.current) {
      collapsedForContentRef.current = true;
      setIsOpen(false);
    }
  }, [content]);

  if (!shouldShow) {
    return null;
  }
  const label = reasoning ? "思考过程" : "正在思考";
  return (
    <details
      className={isOpen ? "chat-reasoning msg-reasoning" : "chat-reasoning msg-reasoning is-collapsed"}
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary className="msg-reasoning-toggle" aria-expanded={isOpen ? "true" : "false"}>
        <span className="msg-reasoning-label">{label}</span>
        {isPending && !content ? <time>{elapsed}</time> : null}
        {isPending && !content ? <Loader2 className="spin" size={12} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
      </summary>
      <div className="chat-reasoning-body msg-reasoning-body">
        {reasoning || "等待模型返回 reasoning..."}
      </div>
    </details>
  );
}

function ChatMessageContent({ message, onImageOpen }) {
  const content = String(message.content || "");
  if (message.error) {
    return (
      <>
        <ChatReasoningBlock message={message} />
        <div className="chat-error chat-message-error" role="status">{content}</div>
      </>
    );
  }
  const shouldCollapse = message.role === "user" && isLongUserMessage(content);
  if (shouldCollapse) {
    return (
      <details className="chat-user-fold">
        <summary>
          <span className="chat-user-preview">{content}</span>
          <span className="chat-user-expand">展开完整消息</span>
        </summary>
        <MarkdownContent content={content} onImageOpen={onImageOpen} />
      </details>
    );
  }
  const retryNotice = String(message.retryNotice || "").trim();
  return (
    <>
      <ChatReasoningBlock message={message} />
      {content ? <MarkdownContent content={content} onImageOpen={onImageOpen} highlightCode={message.role === "assistant"} /> : null}
      <ChatArtifactStrip artifacts={message.artifacts} onImageOpen={onImageOpen} />
      {retryNotice ? <p className="chat-retry-notice">{retryNotice}</p> : null}
      {message.stopped ? <p className="chat-stopped">已停止生成</p> : null}
    </>
  );
}

function normalizeConversationTitle(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function ChatImageDialog({ image, onClose }) {
  useEffect(() => {
    if (!image) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [image, onClose]);

  if (!image || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="chat-image-dialog" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    }}>
      <div className="chat-image-dialog-panel" role="dialog" aria-modal="true" aria-label={image.alt || "图片预览"}>
        <button className="chat-image-dialog-close" type="button" onClick={onClose} aria-label="关闭图片预览">
          <X size={18} aria-hidden="true" />
        </button>
        <img src={image.src} alt={image.alt || "AI 回复图片"} />
      </div>
    </div>,
    document.body,
  );
}

function ChatModelPicker({ value, options, disabled, onChange }) {
  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [focusedValue, setFocusedValue] = useState(value);
  const [supportsHover, setSupportsHover] = useState(false);
  const [isTriggerHovering, setIsTriggerHovering] = useState(false);
  const [isMenuHovering, setIsMenuHovering] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const optionRefs = useRef(new Map());
  const safeOptions = Array.isArray(options) ? options : [];
  const activeOption = useMemo(
    () => safeOptions.find((item) => item.value === value) || safeOptions[0] || null,
    [safeOptions, value],
  );
  const isDisabled = disabled || safeOptions.length === 0;
  const isMenuVisible = isPinnedOpen || (supportsHover && (isTriggerHovering || isMenuHovering));
  const activeLabel = activeOption?.label || "暂无模型";

  useEffect(() => {
    if (isDisabled) {
      if (hoverCloseTimerRef.current) {
        window.clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
      setIsPinnedOpen(false);
      setIsTriggerHovering(false);
      setIsMenuHovering(false);
    }
  }, [isDisabled]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateSupportsHover = () => setSupportsHover(mediaQuery.matches);
    updateSupportsHover();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateSupportsHover);
      return () => mediaQuery.removeEventListener("change", updateSupportsHover);
    }
    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updateSupportsHover);
      return () => mediaQuery.removeListener(updateSupportsHover);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!isMenuVisible) {
      return undefined;
    }
    const closeOnOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      if (hoverCloseTimerRef.current) {
        window.clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
      setIsPinnedOpen(false);
      setIsTriggerHovering(false);
      setIsMenuHovering(false);
    };
    window.addEventListener("pointerdown", closeOnOutside);
    return () => window.removeEventListener("pointerdown", closeOnOutside);
  }, [isMenuVisible]);

  useEffect(() => {
    if (!isMenuVisible) {
      return undefined;
    }
    const updateMenuStyle = () => {
      const trigger = triggerRef.current;
      if (!trigger) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const padding = 8;
      const gap = 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const mobile = viewportWidth <= 640;
      const width = mobile
        ? Math.min(320, viewportWidth - padding * 2)
        : Math.min(300, Math.max(244, rect.width + 72));
      const left = mobile
        ? padding
        : Math.max(padding, Math.min(rect.left, viewportWidth - width - padding));
      const top = Math.min(rect.bottom + gap, viewportHeight - 96);
      const maxHeight = Math.max(180, Math.min(360, viewportHeight - top - padding));
      setMenuStyle({
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        maxHeight: `${maxHeight}px`,
      });
    };
    updateMenuStyle();
    window.requestAnimationFrame(updateMenuStyle);
    window.addEventListener("resize", updateMenuStyle);
    window.addEventListener("scroll", updateMenuStyle, true);
    return () => {
      window.removeEventListener("resize", updateMenuStyle);
      window.removeEventListener("scroll", updateMenuStyle, true);
    };
  }, [isMenuVisible, safeOptions.length]);

  useEffect(() => {
    if (!isPinnedOpen || !isMenuVisible) {
      return;
    }
    const nextValue = activeOption?.value || safeOptions[0]?.value || "";
    setFocusedValue(nextValue);
    window.requestAnimationFrame(() => {
      optionRefs.current.get(nextValue)?.focus();
    });
  }, [activeOption, isMenuVisible, isPinnedOpen, safeOptions]);

  function clearHoverCloseTimer() {
    if (hoverCloseTimerRef.current) {
      window.clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }

  function closeMenu(focusTrigger = false) {
    clearHoverCloseTimer();
    setIsPinnedOpen(false);
    setIsTriggerHovering(false);
    setIsMenuHovering(false);
    if (focusTrigger) {
      window.requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    }
  }

  function openPinnedMenu() {
    clearHoverCloseTimer();
    setIsTriggerHovering(false);
    setIsMenuHovering(false);
    setIsPinnedOpen(true);
  }

  function selectOption(nextValue) {
    if (isDisabled || !safeOptions.some((item) => item.value === nextValue)) {
      return;
    }
    onChange(nextValue);
    closeMenu();
  }

  function moveFocus(direction) {
    const currentIndex = Math.max(0, safeOptions.findIndex((item) => item.value === focusedValue));
    const nextIndex = direction === "prev"
      ? Math.max(0, currentIndex - 1)
      : Math.min(safeOptions.length - 1, currentIndex + 1);
    const nextValue = safeOptions[nextIndex]?.value;
    if (!nextValue) {
      return;
    }
    setFocusedValue(nextValue);
    optionRefs.current.get(nextValue)?.focus();
  }

  function handleTriggerKeyDown(event) {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPinnedMenu();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
    }
  }

  function handleMenuKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus("next");
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus("prev");
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectOption(focusedValue);
    }
  }

  function handlePointerEnter() {
    if (!supportsHover || isDisabled) {
      return;
    }
    clearHoverCloseTimer();
    setIsTriggerHovering(true);
  }

  function handlePointerLeave() {
    if (!supportsHover || isDisabled || isPinnedOpen) {
      return;
    }
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = window.setTimeout(() => {
      setIsTriggerHovering(false);
      setIsMenuHovering(false);
      hoverCloseTimerRef.current = null;
    }, 96);
  }

  function handleMenuPointerEnter() {
    if (!supportsHover || isDisabled) {
      return;
    }
    clearHoverCloseTimer();
    setIsMenuHovering(true);
  }

  function handleMenuPointerLeave() {
    if (!supportsHover || isDisabled || isPinnedOpen) {
      return;
    }
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = window.setTimeout(() => {
      setIsTriggerHovering(false);
      setIsMenuHovering(false);
      hoverCloseTimerRef.current = null;
    }, 96);
  }

  return (
    <div
      className={isMenuVisible ? "chat-model-picker is-open" : "chat-model-picker"}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <button
        ref={triggerRef}
        className="chat-model-trigger"
        type="button"
        disabled={isDisabled}
        aria-label={`选择聊天模型，当前 ${activeLabel}`}
        aria-haspopup="listbox"
        aria-expanded={isMenuVisible}
        onClick={() => {
          clearHoverCloseTimer();
          setIsTriggerHovering(false);
          setIsMenuHovering(false);
          setIsPinnedOpen((current) => !current);
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <MessageCircle size={16} aria-hidden="true" />
      </button>
      <span className="chat-model-trigger-label" aria-hidden="true">
        {activeLabel}
      </span>
      {isMenuVisible && !isDisabled && typeof document !== "undefined"
        ? createPortal(
          <div
            className="chat-model-menu"
            ref={menuRef}
            role="listbox"
            aria-label="选择聊天模型"
            style={menuStyle || { left: 8, top: 8, width: 280, visibility: "hidden" }}
            onPointerEnter={handleMenuPointerEnter}
            onPointerLeave={handleMenuPointerLeave}
            onKeyDown={handleMenuKeyDown}
          >
            <div className="chat-model-options">
              {safeOptions.map((item) => {
                const selected = item.value === value;
                return (
                  <button
                    key={item.value}
                    ref={(node) => {
                      if (node) {
                        optionRefs.current.set(item.value, node);
                      } else {
                        optionRefs.current.delete(item.value);
                      }
                    }}
                    className={selected ? "chat-model-option active" : "chat-model-option"}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    tabIndex={focusedValue === item.value ? 0 : -1}
                    onClick={() => selectOption(item.value)}
                    onFocus={() => setFocusedValue(item.value)}
                    onMouseEnter={() => setFocusedValue(item.value)}
                  >
                    <MessageCircle size={15} aria-hidden="true" />
                    <span>{item.label}</span>
                    {selected ? <Check size={15} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function ChatWorkspace({
  conversations,
  activeConversationId,
  model,
  modelOptions,
  isSending,
  onModelChange,
  onDeleteMessage,
  onStop,
  onSubmit,
}) {
  const [draft, setDraft] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [copiedMessageId, setCopiedMessageId] = useState("");
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [chatComposerAreaHeight, setChatComposerAreaHeight] = useState(0);
  const composerTextareaRef = useRef(null);
  const chatScrollportRef = useRef(null);
  const chatMessageListRef = useRef(null);
  const chatComposerAreaRef = useRef(null);
  const chatAttachmentInputRef = useRef(null);
  const attachmentsRef = useRef([]);
  const scrollBottomUpdaterRef = useRef(() => {});
  const pendingScrollTargetRef = useRef(null);
  const activeConversation = conversations.find((item) => item.id === activeConversationId) || null;
  const messages = activeConversation?.messages || [];
  const uploadingAttachments = attachments.some((item) => item.status === "uploading");
  const readyAttachments = attachments.filter((item) => item.status === "ready" && item.url);
  const canSend = (draft.trim().length > 0 || readyAttachments.length > 0)
    && !uploadingAttachments
    && !isSending
    && Boolean(model)
    && modelOptions.length > 0;

  const pendingAnchorMessage = pendingScrollTargetRef.current?.conversationId === activeConversationId
    ? messages.find((message) => message.id === pendingScrollTargetRef.current?.assistantMessageId)
    : null;
  const shouldReserveAnchorSpace = Boolean(
    pendingAnchorMessage
      && pendingAnchorMessage.pending
      && !String(pendingAnchorMessage.content || "").trim(),
  );

  function getChatContentBottomTop(scrollport) {
    const list = chatMessageListRef.current;
    if (!(list instanceof HTMLElement)) {
      return Math.max(0, scrollport.scrollHeight - scrollport.clientHeight);
    }
    const lastContentNode = list.lastElementChild;
    if (lastContentNode instanceof HTMLElement) {
      const portRect = scrollport.getBoundingClientRect();
      const contentRect = lastContentNode.getBoundingClientRect();
      return Math.max(0, scrollport.scrollTop + (contentRect.bottom - portRect.top) - scrollport.clientHeight);
    }
    const portRect = scrollport.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    return Math.max(0, scrollport.scrollTop + (listRect.bottom - portRect.top) - scrollport.clientHeight);
  }

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => () => {
    cleanupAttachmentPreviews(attachmentsRef.current);
  }, []);

  useEffect(() => {
    const composerArea = chatComposerAreaRef.current;
    if (!composerArea) {
      return undefined;
    }
    const updateComposerAreaHeight = () => {
      setChatComposerAreaHeight(Math.max(0, Math.round(composerArea.getBoundingClientRect().height)));
    };
    updateComposerAreaHeight();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateComposerAreaHeight);
      observer.observe(composerArea);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", updateComposerAreaHeight);
    return () => window.removeEventListener("resize", updateComposerAreaHeight);
  }, []);

  useEffect(() => {
    const scrollport = chatScrollportRef.current;
    if (!scrollport) {
      return undefined;
    }
    const updateScrollButtonVisibility = () => {
      const distanceFromBottom = Math.max(0, getChatContentBottomTop(scrollport) - scrollport.scrollTop);
      setShowScrollToBottom(distanceFromBottom > CHAT_SCROLL_BOTTOM_THRESHOLD);
    };
    scrollBottomUpdaterRef.current = updateScrollButtonVisibility;
    updateScrollButtonVisibility();
    scrollport.addEventListener("scroll", updateScrollButtonVisibility, { passive: true });
    window.addEventListener("resize", updateScrollButtonVisibility);
    return () => {
      scrollport.removeEventListener("scroll", updateScrollButtonVisibility);
      window.removeEventListener("resize", updateScrollButtonVisibility);
      if (scrollBottomUpdaterRef.current === updateScrollButtonVisibility) {
        scrollBottomUpdaterRef.current = () => {};
      }
    };
  }, []);

  useEffect(() => {
    scrollBottomUpdaterRef.current?.();
  }, [messages, draft, attachments, chatComposerAreaHeight]);

  useLayoutEffect(() => {
    const pendingScrollTarget = pendingScrollTargetRef.current;
    if (!pendingScrollTarget) {
      return;
    }
    if (pendingScrollTarget.conversationId !== activeConversationId) {
      return;
    }
    const scrollport = chatScrollportRef.current;
    const list = chatMessageListRef.current;
    if (!scrollport || !list) {
      return;
    }
    const messageNode = list.querySelector(`[data-message-id="${pendingScrollTarget.userMessageId}"]`);
    if (!(messageNode instanceof HTMLElement)) {
      return;
    }
    const listRect = scrollport.getBoundingClientRect();
    const messageRect = messageNode.getBoundingClientRect();
    const topGap = Math.max(72, Math.round(scrollport.clientHeight * 0.28));
    const maxTop = Math.max(0, scrollport.scrollHeight - scrollport.clientHeight);
    const nextTop = Math.max(0, Math.min(maxTop, scrollport.scrollTop + (messageRect.top - listRect.top) - topGap));
    scrollport.scrollTo({
      top: nextTop,
      behavior: "auto",
    });
    scrollBottomUpdaterRef.current?.();
    pendingScrollTargetRef.current = null;
  }, [activeConversationId, messages]);

  useEffect(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "auto";
    const minHeight = 68;
    const viewportLimit = typeof window === "undefined" ? 160 : Math.round(window.innerHeight * 0.28);
    const maxHeight = Math.min(180, Math.max(112, viewportLimit));
    const nextHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [draft]);

  async function addChatFiles(fileList, kindHint = "file") {
    const files = Array.from(fileList || []).slice(0, Math.max(0, MAX_CHAT_ATTACHMENTS - attachmentsRef.current.length));
    if (files.length === 0) {
      return;
    }
    const drafts = files.map((file) => createAttachmentDraft(file, kindHint));
    attachmentsRef.current = [...attachmentsRef.current, ...drafts].slice(0, MAX_CHAT_ATTACHMENTS);
    setAttachments(attachmentsRef.current);
    await Promise.all(drafts.map(async (draft, index) => {
      try {
        const uploaded = await uploadChatAttachment(files[index]);
        setAttachments((current) => {
          const next = current.map((item) => (
            item.id === draft.id ? normalizeUploadedAttachment(item, uploaded) : item
          ));
          attachmentsRef.current = next;
          return next;
        });
      } catch (error) {
        setAttachments((current) => {
          const next = current.map((item) => (
            item.id === draft.id
              ? {
                ...item,
                status: "error",
                error: error instanceof Error ? error.message : "上传失败",
              }
              : item
          ));
          attachmentsRef.current = next;
          return next;
        });
      }
    }));
  }

  function removeChatAttachment(id) {
    setAttachments((current) => {
      const removed = current.find((item) => item.id === id);
      revokeAttachmentPreview(removed);
      const next = current.filter((item) => item.id !== id);
      attachmentsRef.current = next;
      return next;
    });
  }

  function clearChatAttachments() {
    cleanupAttachmentPreviews(attachmentsRef.current);
    attachmentsRef.current = [];
    setAttachments([]);
  }

  async function copyMessage(message) {
    const text = messageTextForCopy(message);
    if (!text) {
      return;
    }
    await copyTextToClipboard(text);
    setCopiedMessageId(message.id);
    window.setTimeout(() => setCopiedMessageId((current) => (current === message.id ? "" : current)), 1400);
  }

  function scrollChatToBottom() {
    const scrollport = chatScrollportRef.current;
    if (!scrollport) {
      return;
    }
    scrollport.scrollTo({
      top: getChatContentBottomTop(scrollport),
      behavior: "smooth",
    });
    window.requestAnimationFrame(() => {
      scrollBottomUpdaterRef.current?.();
    });
  }

  function submitDraft() {
    if (!canSend) {
      return;
    }
    pendingScrollTargetRef.current = onSubmit(draft, readyAttachments) || null;
    setDraft("");
    clearChatAttachments();
  }

  function handleSubmit(event) {
    event.preventDefault();
    submitDraft();
  }

  function handleComposerKeyDown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) {
      return;
    }
    event.preventDefault();
    submitDraft();
  }

  return (
    <section className="chat-workspace" aria-labelledby="chat-workspace-heading">
      <h2 className="sr-only" id="chat-workspace-heading">对话工作区</h2>
      <div
        className="chat-panel"
        style={chatComposerAreaHeight > 0 ? { "--chat-composer-offset": `${chatComposerAreaHeight}px` } : undefined}
      >
        <div className="chat-floating-controls" aria-label="当前聊天模型">
          <ChatModelPicker
            value={model}
            options={modelOptions}
            onChange={onModelChange}
            disabled={isSending || modelOptions.length === 0}
          />
        </div>

        <div
          ref={chatScrollportRef}
          className="chat-scrollport"
          style={{ "--chat-anchor-reserve": shouldReserveAnchorSpace ? "clamp(180px, 42dvh, 420px)" : "0px" }}
        >
          <div
            ref={chatMessageListRef}
            className={messages.length > 0 ? "chat-message-list" : "chat-message-list is-empty"}
          >
          {messages.length > 0 ? (
            messages.map((message) => {
              const messageArticle = (
                <article className="chat-message">
                  <ChatMessageContent message={message} onImageOpen={setPreviewImage} />
                  {!message.pending ? (
                    <ChatMessageActions
                      message={message}
                      copied={copiedMessageId === message.id}
                      onCopy={copyMessage}
                      onDelete={(targetMessage) => onDeleteMessage?.(activeConversation?.id || activeConversationId, targetMessage.id)}
                    />
                  ) : null}
                </article>
              );
              return (
                <div
                  className={message.role === "user" ? "chat-turn is-user" : "chat-turn is-assistant"}
                  data-message-id={message.id}
                  data-message-role={message.role}
                  key={message.id}
                >
                  {message.role === "user" ? (
                    <div className="chat-message-stack">
                      <ChatAttachmentStrip
                        attachments={message.attachments}
                        className="chat-message-attachments"
                        onImageOpen={setPreviewImage}
                      />
                      {messageArticle}
                    </div>
                  ) : messageArticle}
                  {message.role === "user" ? (
                    <div className="chat-avatar" aria-hidden="true">
                      <UserRound size={18} />
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            <section className="chat-welcome" aria-label="欢迎使用 AI 聊天">
              <div className="chat-welcome-copy">
                <h2>
                  <span>你好，我不是</span>
                  <strong>豆包</strong>
                  <Hand className="chat-welcome-wave" size={38} strokeWidth={2.6} aria-hidden="true" />
                </h2>
                <p className="chat-welcome-subtitle">一个简洁、专注、高效的 AI 聊天应用</p>
                <p>对话直接AI交互，不记录也不缓存对话内容，有价值的信息内容及时保存。图片生成请切换生图页面</p>
              </div>
              <div className="chat-welcome-visual" aria-hidden="true">
                <div className="chat-welcome-panel"></div>
                <div className="chat-welcome-bubble">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="chat-welcome-profile">
                  <span className="chat-welcome-avatar">
                    <span></span>
                  </span>
                  <span className="chat-welcome-lines">
                    <i></i>
                    <i></i>
                    <i></i>
                  </span>
                </div>
                <div className="chat-welcome-input">
                  <span></span>
                  <i></i>
                </div>
                <span className="chat-welcome-spark chat-welcome-spark-one"></span>
                <span className="chat-welcome-spark chat-welcome-spark-two"></span>
                <span className="chat-welcome-spark chat-welcome-spark-three"></span>
              </div>
            </section>
          )}
          </div>
        </div>
        {messages.length > 0 ? (
          <button
            className={showScrollToBottom ? "chat-scroll-bottom-button is-visible" : "chat-scroll-bottom-button"}
            type="button"
            onClick={scrollChatToBottom}
            aria-label="滚动到最新消息"
          >
            <ArrowDown size={18} aria-hidden="true" />
          </button>
        ) : null}

        <div className="chat-composer-area" ref={chatComposerAreaRef}>
        <input
          ref={chatAttachmentInputRef}
          className="sr-only"
          type="file"
          accept={CHAT_ATTACHMENT_ACCEPT}
          multiple
          onChange={(event) => {
            addChatFiles(event.target.files, "file");
            event.target.value = "";
          }}
        />
        <ChatAttachmentStrip
          attachments={attachments}
          className="chat-composer-attachments"
          onImageOpen={setPreviewImage}
          onRemove={removeChatAttachment}
        />
        <form className="chat-composer" onSubmit={handleSubmit}>
          <label className="sr-only" htmlFor="chat-message-input">输入你的想法或需求，Enter发送，Shift + Enter换行</label>
          <textarea
            id="chat-message-input"
            ref={composerTextareaRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            placeholder={modelOptions.length > 0 ? "输入你的想法或需求，Enter发送，Shift + Enter换行" : "请先在模型策略中启用文本生成或多模态模型"}
            disabled={modelOptions.length === 0}
            rows={2}
          />
          <div className="chat-composer-footer">
            <div className="chat-tool-row">
              <button
                className="chat-tool-chip"
                type="button"
                onClick={() => chatAttachmentInputRef.current?.click()}
                disabled={attachments.length >= MAX_CHAT_ATTACHMENTS || isSending}
                aria-label="上传附件或图片"
              >
                <Paperclip size={16} />
                附件
              </button>
            </div>
            <div className="chat-send-area">
              <span className="chat-count">{draft.length} / 4000</span>
              <button
                className={isSending ? "chat-send-button is-stopping" : "chat-send-button"}
                type={isSending ? "button" : "submit"}
                disabled={isSending ? false : !canSend}
                onClick={isSending ? onStop : undefined}
                aria-label={isSending ? "停止生成" : "发送对话"}
              >
                {isSending ? <Square size={15} aria-hidden="true" /> : <Send size={17} aria-hidden="true" />}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
      <ChatImageDialog image={previewImage} onClose={() => setPreviewImage(null)} />
    </section>
  );
}

function LoadingState({ progress }) {
  return (
    <div className="loading-state">
      <div className="black-mirror-loading" aria-hidden="true">
        {Array.from({ length: 8 }, (_, index) => (
          <div className="part" key={index} />
        ))}
      </div>
      <p>{progress || GENERATE_PROGRESS.default}</p>
    </div>
  );
}

function ResultPreview({ item, currentIndex, historyLength, onNavigate, onDelete }) {
  const [copied, setCopied] = useState(false);
  const rejectionText = getGenerationTextResult(item);
  const src = getDisplayImageSrc(item);
  const placeholderSrc = getPreviewPlaceholderSrc(item);
  const downloadSrc = getDownloadImageSrc(item);
  const previewSrc = useProgressiveImageSrc(src, placeholderSrc);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex >= 0 && currentIndex < historyLength - 1;

  async function copyPrompt() {
    if (!item.prompt) {
      return;
    }
    try {
      await copyTextToClipboard(item.prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.warn("Failed to copy prompt", error);
    }
  }

  if (rejectionText) {
    return (
      <div className="result-preview result-preview-text">
        <div className="result-text-wrap">
          <div className="result-text-panel" role="status">
            <div className="result-text-label">
              <AlertTriangle size={18} aria-hidden="true" />
              <span>请求被拒绝</span>
            </div>
            <p>{rejectionText}</p>
          </div>
        </div>
        {historyLength > 1 && (
          <>
            <button
              className="preview-arrow preview-arrow-left"
              type="button"
              disabled={!canPrev}
              onClick={() => onNavigate("prev")}
              title="上一项"
            >
              <ChevronLeft size={26} />
            </button>
            <button
              className="preview-arrow preview-arrow-right"
              type="button"
              disabled={!canNext}
              onClick={() => onNavigate("next")}
              title="下一项"
            >
              <ChevronRight size={26} />
            </button>
          </>
        )}
        <div className="preview-info">
          <div className="preview-meta">
            <p>{item.prompt || "生成请求"}</p>
            <div>
              <span>{item.aspectRatio || "text"}</span>
              <span>•</span>
              <span>{formatTimestamp(item.createdAt)}</span>
              {historyLength > 0 && (
                <>
                  <span>•</span>
                  <span>
                    {currentIndex + 1} / {historyLength}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="preview-actions">
            <button type="button" className="preview-action danger" onClick={onDelete} title="从历史记录中删除">
              <Trash2 size={18} />
            </button>
            <button type="button" className="preview-action" onClick={copyPrompt} disabled={!item.prompt} title="复制提示词">
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="result-preview">
      <div className="preview-backdrop" style={{ backgroundImage: `url(${previewSrc || src})` }} />
      <div className="result-image-wrap">
        <img
          src={previewSrc || src}
          alt="生成结果"
          draggable
          loading="eager"
          referrerPolicy="no-referrer"
          onDragStart={(event) => {
            event.dataTransfer.setData("text/plain", src);
            event.dataTransfer.effectAllowed = "copy";
          }}
        />
      </div>
      {historyLength > 1 && (
        <>
          <button
            className="preview-arrow preview-arrow-left"
            type="button"
            disabled={!canPrev}
            onClick={() => onNavigate("prev")}
            title="上一张"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            className="preview-arrow preview-arrow-right"
            type="button"
            disabled={!canNext}
            onClick={() => onNavigate("next")}
            title="下一张"
          >
            <ChevronRight size={26} />
          </button>
        </>
      )}
      <div className="preview-info">
        <div className="preview-meta">
          <p>{item.prompt || "生成的图像"}</p>
          <div>
            <span>{item.aspectRatio}</span>
            <span>•</span>
            <span>{formatTimestamp(item.createdAt)}</span>
            {historyLength > 0 && (
              <>
                <span>•</span>
                <span>
                  {currentIndex + 1} / {historyLength}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="preview-actions">
          <button type="button" className="preview-action danger" onClick={onDelete} title="从历史记录中删除">
            <Trash2 size={18} />
          </button>
          <button type="button" className="preview-action" onClick={copyPrompt} disabled={!item.prompt} title="复制提示词">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
          <a
            className="preview-action"
            href={downloadSrc || src}
            download={buildDownloadName(item, downloadSrc || src)}
            onClick={(event) => handleImageDownload(event, item, downloadSrc || src)}
            title="下载图片"
          >
            <Download size={18} />
          </a>
        </div>
      </div>
    </div>
  );
}

function SponsoredBar({ settings }) {
  const items = (settings?.ad_overlay_items || []).filter((item) => item?.cta_link);
  if (!settings?.enable_ad_overlay || items.length === 0) {
    return null;
  }
  const item = items[0];
  return (
    <a className="sponsor-bar" href={item.cta_link} target="_blank" rel="noreferrer">
      <span>{settings.ad_sponsored_label_zh || "赞助"}</span>
      <strong>{item.name_zh || item.name_en}</strong>
      <em>{item.cta_text_zh || item.cta_text_en || "查看"}</em>
    </a>
  );
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function createHistoryItem(id, result, payload, aspectOptions) {
  const rejectionText = getGenerationTextResult(result);
  if (rejectionText) {
    return {
      id,
      taskId: result.taskId || id,
      type: "text_rejection",
      resultType: "text_rejection",
      result_type: "text_rejection",
      finishReason: result.finishReason || result.finish_reason || "content_policy",
      finish_reason: result.finish_reason || result.finishReason || "content_policy",
      rejectionText,
      rejection_text: rejectionText,
      text: rejectionText,
      message: rejectionText,
      imageUrl: "",
      displayUrl: "",
      cachedImageUrl: "",
      thumbnailUrl: "",
      prompt: payload.prompt,
      model: payload.model,
      aspectRatio: displayAspectRatio(payload.aspectRatio, aspectOptions),
      createdAt: Date.now(),
    };
  }
  const imageItems = getImageSetItems(result);
  const primaryImage = imageItems[0] || {};
  const displayUrl = result.displayUrl || result.display_url || result.imageUrl || result.image_url || primaryImage.displayUrl || primaryImage.display_url || "";
  const cachedImageUrl = result.cachedImageUrl || result.cached_image_url || primaryImage.cachedImageUrl || primaryImage.cached_image_url || "";
  return {
    id,
    taskId: result.taskId || id,
    imageUrl: result.imageUrl || result.image_url || displayUrl,
    displayUrl,
    cachedImageUrl,
    thumbnailUrl: result.thumbnailUrl || result.thumbnail_url || thumbnailUrlForDisplay(cachedImageUrl || displayUrl),
    images: imageItems,
    requestedImageCount: payload.imageCount || 1,
    actualImageCount: imageItems.length || 1,
    prompt: payload.prompt,
    model: payload.model,
    aspectRatio: displayAspectRatio(payload.aspectRatio, aspectOptions),
    createdAt: Date.now(),
  };
}

function buildAspectRatioOptions(settings, modelId, resolution = STANDARD_RESOLUTION) {
  const modelOption = getModelOption(settings, modelId);
  const sizeMapping = getModelSizeMapping(modelOption, resolution);
  return ASPECT_RATIOS.map((item) => {
    const configured = normalizeOptionalText(sizeMapping?.[item.optionKey] || modelOption?.[item.optionKey]);
    return {
      ...item,
      mappingLabel: configured || item.fallbackMapping,
      isConfigured: Boolean(configured),
    };
  });
}

function buildResolutionOptions(settings, modelId, aspectRatio = "auto") {
  const modelOption = getModelOption(settings, modelId);
  const mappings = Array.isArray(modelOption?.size_mappings) ? modelOption.size_mappings : [];
  const options = [];
  const seen = new Set();
  mappings.forEach((mapping) => {
    const value = normalizeResolutionValue(mapping?.resolution);
    if (!value || seen.has(value)) {
      return;
    }
    seen.add(value);
    const configuredSize = getMappingSizeForAspect(mapping, modelOption, aspectRatio);
    const creditCost = getMappingCreditCost(mapping, modelOption);
    options.push({
      value,
      label: normalizeOptionalText(mapping?.label) || (value === STANDARD_RESOLUTION ? "标准" : value.toUpperCase()),
      description: configuredSize || "",
      meta: `${creditCost} 积分`,
      icon: Monitor,
    });
  });
  if (options.length) {
    return options;
  }
  if (modelOption) {
    const configuredSize = getMappingSizeForAspect(null, modelOption, aspectRatio);
    const creditCost = getMappingCreditCost(null, modelOption);
    return [{
      value: STANDARD_RESOLUTION,
      label: "标准",
      description: configuredSize || "",
      meta: `${creditCost} 积分`,
      icon: Monitor,
    }];
  }
  return DEFAULT_ADVANCED_RESOLUTION_OPTIONS;
}

function buildBackgroundOptions(modelId) {
  if (modelSupportsTransparentBackground(modelId)) {
    return ADVANCED_BACKGROUND_OPTIONS;
  }
  return ADVANCED_BACKGROUND_OPTIONS.filter((item) => item.value !== "transparent");
}

function buildAdvancedGeneratePayload(values) {
  const resolution = normalizeResolutionValue(values?.resolution);
  const payload = { resolution };
  if (resolution === STANDARD_RESOLUTION) {
    return payload;
  }
  const negativePrompt = normalizeOptionalText(values?.negativePrompt);
  const style = normalizeOptionalText(values?.style);
  const quality = normalizeOptionalText(values?.quality);
  const background = normalizeBackgroundValue(values?.background, values?.model);
  const sampler = normalizeOptionalText(values?.sampler);
  const steps = Number(values?.steps);
  if (negativePrompt) {
    payload.negative_prompt = negativePrompt;
  }
  if (style) {
    payload.style = style;
  }
  if (quality) {
    payload.quality = quality;
  }
  if (background) {
    payload.background = background;
  }
  if (Number.isFinite(steps) && steps > 0) {
    payload.steps = Math.floor(steps);
  }
  if (sampler) {
    payload.sampler = sampler;
  }
  return payload;
}

function normalizeBackgroundValue(value, modelId) {
  const background = normalizeOptionalText(value).toLowerCase();
  if (!background || background === "auto" || !ADVANCED_BACKGROUND_VALUES.has(background)) {
    return "";
  }
  if (background === "transparent" && !modelSupportsTransparentBackground(modelId)) {
    return "";
  }
  return background;
}

function modelSupportsTransparentBackground(modelId) {
  const normalized = normalizeOptionalText(modelId).toLowerCase();
  return Boolean(normalized) && !MODELS_WITHOUT_TRANSPARENT_BACKGROUND.has(normalized);
}

function getModelOption(settings, modelId) {
  return (settings?.model_options || []).find((item) => item.id === modelId) || null;
}

function getModelSizeMapping(modelOption, resolution) {
  const mappings = Array.isArray(modelOption?.size_mappings) ? modelOption.size_mappings : [];
  const normalizedResolution = normalizeResolutionValue(resolution);
  return mappings.find((item) => normalizeResolutionValue(item?.resolution) === normalizedResolution)
    || mappings.find((item) => normalizeResolutionValue(item?.resolution) === STANDARD_RESOLUTION)
    || null;
}

function getMappingCreditCost(mapping, modelOption) {
  const cost = Number(mapping?.credit_cost ?? modelOption?.credit_cost ?? 1);
  return Number.isFinite(cost) && cost >= 0 ? Math.floor(cost) : 1;
}

function getMappingSizeForAspect(mapping, modelOption, aspectRatio = "auto") {
  const matchedAspect = ASPECT_RATIOS.find((item) => item.value === aspectRatio)
    || ASPECT_RATIOS.find((item) => item.value === "auto");
  if (!matchedAspect) {
    return "";
  }
  return normalizeOptionalText(mapping?.[matchedAspect.optionKey] || modelOption?.[matchedAspect.optionKey])
    || matchedAspect.fallbackMapping;
}

function normalizeResolutionValue(value) {
  return String(value || STANDARD_RESOLUTION).trim().toLowerCase().replace(/[\s_]+/g, "-") || STANDARD_RESOLUTION;
}

function normalizeOptionalText(value) {
  const text = String(value || "").trim();
  return text && text.toLowerCase() !== "null" ? text : "";
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) {
      return number;
    }
  }
  return 0;
}

function authInfoLoggedOut(currentAuthInfo) {
  return {
    ...(currentAuthInfo || {}),
    authenticated: false,
    user: null,
  };
}

function authInfoWithUser(currentAuthInfo, user) {
  if (!user || typeof user !== "object") {
    return null;
  }
  return {
    ...(currentAuthInfo || {}),
    authenticated: true,
    user: {
      ...(currentAuthInfo?.user || {}),
      ...user,
    },
  };
}

function mergePreferredAuthInfo(nextAuth, preferredAuthInfo) {
  if (!preferredAuthInfo?.user || !nextAuth?.user || preferredAuthInfo.user.id !== nextAuth.user.id) {
    return nextAuth || preferredAuthInfo || null;
  }
  const preferredCredits = Number(preferredAuthInfo.user.credits);
  const nextCredits = Number(nextAuth.user.credits);
  if (Number.isFinite(preferredCredits) && (!Number.isFinite(nextCredits) || preferredCredits > nextCredits)) {
    return {
      ...nextAuth,
      authenticated: true,
      user: {
        ...nextAuth.user,
        credits: preferredCredits,
      },
    };
  }
  return nextAuth;
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const copied = document.execCommand("copy");
    if (!copied) {
      throw new Error("copy_command_failed");
    }
  } finally {
    textarea.remove();
  }
}

function displayAspectRatio(value, options) {
  const option = options.find((item) => item.value === value);
  return option?.label || value || "自动";
}

function getAuthScopeKey(authInfo) {
  if (!authInfo) {
    return "loading";
  }
  if (authInfo.authenticated && authInfo.user?.id) {
    return `user:${authInfo.user.id}`;
  }
  return "guest";
}

function statusLabel(status) {
  if (status === "waiting") {
    return GENERATE_PROGRESS.waiting;
  }
  if (status === "completed") {
    return GENERATE_PROGRESS.completed;
  }
  if (status === "failed") {
    return GENERATE_PROGRESS.failed;
  }
  return GENERATE_PROGRESS.pending;
}

export default App;
